import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Card,
  CardBody,
  Container,
  Form,
  FormGroup,
  Label,
  Row,
  Col,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from "reactstrap";
import Select from "react-select";
import TableContainer from "../../components/Common/TableContainer";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getDepartments } from "../../components/Api/GmsDepartment";
import { getEmployees } from "../../components/Api/GmsEmployee";
import { getAbsentDays, saveAbsentDay } from "../../components/Api/GmsAbsentDay";
import { getAbsentDaysForPayrollPeriod } from "../../components/Api/GmsDailyAttendance";
import { getPayrollDetails, savePayrollDetail } from "../../components/Api/GmsPayrollDetail";
import { getEmployeeLeaves } from "../../components/Api/GmsEmployeeLeave";
import { getLeaveTypes } from "../../components/Api/GmsLeaveType";
import { checkPayrollDateUpdate, createFirstPayroll, updatePayrollDate, skipPayroll } from "../../components/Api/GmsPayroll";
import axios from "axios";
import BASE_URL from "../../config";
import jsPDF from 'jspdf';
import {
  hasPagePermissionWithAdmin,
  hasViewOnlyPermission,
  hasEditOrHigherPermission,
  hasDeletePermissionWithAdmin,
  hasApprovePermissionWithAdmin,
  isUserAdmin
} from "../../components/Permission/PermissionService";

// Get user session
const getUserSession = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null' || value === 'undefined') {
      return null;
    }
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;
    return isNaN(numValue) || numValue <= 0 ? null : numValue;
  };

  let businessId, branchId;

  if (authUser && Object.keys(authUser).length > 0) {
    businessId = normalizeId(authUser.companyId);
    branchId = normalizeId(authUser.branchId);
  } else {
    businessId = normalizeId(user.user_Company_Id || localStorage.getItem("userCompanyId"));
    branchId = normalizeId(user.user_Branch_Id || localStorage.getItem("userBranchId"));
  }

  return {
    userId: authUser.userId || 1,
    userName: authUser.userName || "System",
    businessId: businessId,
    branchId: branchId,
    companyId: businessId,
  };
};

const getAuthHeader = () => {
  const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
  return {
    'Authorization': `Bearer ${tokenData.accessToken}`,
    'Content-Type': 'application/json'
  };
};

// NEW: Add the payroll calculation API (BATCH PROCESSING)
const calculatePayrollData = async (employeeList) => {
  try {
    const tokenData = JSON.parse(localStorage.getItem("authUser") || "{}");
    const request = {
      employees: employeeList.map(emp => ({
        employee_Id: emp.employee_Id,
        absent_Days: emp.absent_Days || 0
      }))
    };

    const response = await axios.post(
      `${BASE_URL}/api/PayrollDetail/calculate-payroll-data`,
      request,
      {
        headers: {
          'Authorization': `Bearer ${tokenData.accessToken}`,
          'Content-Type': 'application/json'
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error in calculatePayrollData:", error);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
      data: null
    };
  }
};

const PayrollProcessingPage = () => {
  const [loading, setLoading] = useState(false);
  const [payrollInfo, setPayrollInfo] = useState(null);
  const currencyCode = localStorage.getItem("currencyCode") || "AED";
  const [businesses, setBusinesses] = useState([]);
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  // Modal state for Add Employee
  const [showAddEmployeeModal, setShowAddEmployeeModal] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [designations, setDesignations] = useState([]);
  const [editableFields, setEditableFields] = useState({});

  // New state for manage modal
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [additionalComponents, setAdditionalComponents] = useState([]);
  const [payrollOtherDetails, setPayrollOtherDetails] = useState([]);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsPayrollProcessing');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsPayrollProcessing');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsPayrollProcessing');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsPayrollProcessing');
  const hasApprovePermission = hasApprovePermissionWithAdmin(permissions, '/GmsPayrollProcessing');
  const isAdmin = isUserAdmin();
  const session = useMemo(() => getUserSession(), []);

  // New state for Payslip modal
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslipEmployee, setSelectedPayslipEmployee] = useState(null);

  // State for date selection modal
  const [showDateSelectionModal, setShowDateSelectionModal] = useState(false);
  const [dateSelectionMode, setDateSelectionMode] = useState(null); // 'create' or 'update'
  const [selectedPayrollDate, setSelectedPayrollDate] = useState(null);
  const [selectedFromDate, setSelectedFromDate] = useState(null);
  const [isMonthly, setIsMonthly] = useState(false);
  const [currentPayrollDateForUpdate, setCurrentPayrollDateForUpdate] = useState(null);

  useEffect(() => {
    if (hasPermission) {
      loadInitialData();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission]);

  // Auto-populate company if user has companyId
  useEffect(() => {
    if (session.businessId && businesses.length > 0 && !selectedBusiness) {
      const business = businesses.find(b => b.value === session.businessId);
      if (business) {
        setSelectedBusiness(business);
      }
    }
  }, [businesses, session.businessId]);

  // Auto-populate branch if user has branchId
  useEffect(() => {
    if (session.branchId && branches.length > 0 && selectedBusiness && !selectedBranch) {
      const branch = branches.find(b => b.value === session.branchId);
      if (branch) {
        setSelectedBranch(branch);
      }
    }
  }, [branches, session.branchId, selectedBusiness]);

  useEffect(() => {
    if (payrollInfo && designations.length > 0) {
      loadPayrollEmployees();
      setHasAppliedFilter(true);
    }
  }, [payrollInfo, designations]);

  const loadInitialData = async () => {
    try {
      await Promise.all([
        loadCurrentPayroll(),
        loadBusinesses(),
        loadDesignations(),
      ]);
    } catch (error) {
      console.error("Error loading initial data:", error);
    }
  };

  const loadCurrentPayroll = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Payroll/get`, {}, {
        headers: getAuthHeader(),
      });

      // Ensure response.data is an array
      const payrollData = Array.isArray(response.data) ? response.data : [];

      // Get the current active payroll (IsPosted = false)
      const currentPayroll = payrollData.find(p => !p.payroll_IsPosted);

      // If no payroll exists, check if we need to create first payroll
      if (!currentPayroll) {
        try {
          const checkResult = await checkPayrollDateUpdate(null);
          // Show date selection modal if we need to create first payroll
          // Check for various message formats that indicate first payroll is needed
          if (checkResult && (
            (checkResult.message && checkResult.message.includes("First payroll needs to be created")) ||
            (checkResult.message && checkResult.message.includes("No payroll found")) ||
            (!checkResult.needsUpdate && payrollData.length === 0)
          )) {
            // Show date selection modal for creating first payroll
            setDateSelectionMode('create');
            setShowDateSelectionModal(true);
            setPayrollInfo(null);
            return;
          }
        } catch (checkError) {
          // If check fails but we have no payroll data, still show the create modal
          console.warn("Error checking payroll date update, but showing create modal anyway:", checkError);
          if (payrollData.length === 0) {
            setDateSelectionMode('create');
            setShowDateSelectionModal(true);
            setPayrollInfo(null);
            return;
          }
        }
        setPayrollInfo(null);
        return;
      }

      setPayrollInfo(currentPayroll);

      // Check if date update is needed (wrap in try-catch to prevent errors from blocking payroll load)
      try {
        const checkResult = await checkPayrollDateUpdate(currentPayroll.payroll_Id);
        // TEMPORARILY DISABLED (per request): "Update Payroll Date" prompt/modal trigger.
        // This is the flow that shows:
        // "The current payroll date is outdated and no employees have been processed yet.
        //  You can either update the date or skip this payroll month."
        //
        // if (checkResult && checkResult.needsUpdate && !checkResult.hasPayrollDetails) {
        //   // Show date selection modal for updating date
        //   setDateSelectionMode('update');
        //   setCurrentPayrollDateForUpdate(currentPayroll.payroll_Date);
        //   setShowDateSelectionModal(true);
        // }
      } catch (checkError) {
        // Log but don't block - payroll info is already loaded successfully
        console.warn("Error checking payroll date update (non-critical):", checkError);
      }
    } catch (error) {
      console.error("Error loading payroll:", error);
      // If it's a 404 or empty response, try to show the create modal
      if (error.response?.status === 404 || error.response?.status === 204 ||
        (error.response?.data && Array.isArray(error.response.data) && error.response.data.length === 0)) {
        // No payroll data exists, show create modal
        setDateSelectionMode('create');
        setShowDateSelectionModal(true);
        setPayrollInfo(null);
      } else {
        Swal.fire("Error", "Failed to load payroll information", "error");
      }
    }
  };

  const loadBusinesses = async () => {
    try {
      const response = await getBusinesses({ business_Active: true });
      const data = Array.isArray(response) ? response : (response.data || []);

      // Fetch company data for each business to get logo information (for fallback)
      const businessOptions = await Promise.all(
        data.map(async (b) => {
          try {
            // Get company data for this business (for fallback logo)
            const companyResponse = await axios.post(`${BASE_URL}/api/Company/get`, {
              Company_Id: b.business_Company_Id
            }, { headers: getAuthHeader() });

            const companyData = Array.isArray(companyResponse.data) ? companyResponse.data[0] : companyResponse.data;

            console.log(`Company data for business ${b.business_Id}:`, companyData);

            return {
              value: b.business_Id,
              label: b.business_NameEn,
              business_LogoPath: b.business_LogoPath || null,
              company: companyData || null
            };
          } catch (error) {
            console.error("Error fetching company data for business:", b.business_Id, error);
            return {
              value: b.business_Id,
              label: b.business_NameEn,
              business_LogoPath: b.business_LogoPath || null,
              company: null
            };
          }
        })
      );

      setBusinesses(businessOptions);
    } catch (error) {
      console.error("Error loading businesses:", error);
      Swal.fire("Error", "Failed to load businesses", "error");
    }
  };

  const loadBranches = async (businessId) => {
    try {
      const response = await getBranches({ Business_Id: businessId, branch_Active: true });
      let branchData = Array.isArray(response) ? response : (response.data || []);

      // Filter strictly for active branches belonging to the selected business
      branchData = branchData.filter(b =>
        b.branch_Active === true &&
        Number(b.branch_Buisness_Id) === Number(businessId)
      );

      const branchOptions = branchData.map(b => ({
        value: b.branch_Id,
        label: b.branch_NameEn || b.branch_Name
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error loading branches:", error);
      Swal.fire("Error", "Failed to load branches", "error");
    }
  };

  const loadDepartments = async (branchId) => {
    try {
      const response = await getDepartments({ Branch_Id: branchId });
      const data = Array.isArray(response) ? response : (response.data || []);
      const departmentOptions = data.map(d => ({
        value: d.department_Id,
        label: d.department_NameEn || d.department_Name
      }));
      setDepartments(departmentOptions);
    } catch (error) {
      console.error("Error loading departments:", error);
      Swal.fire("Error", "Failed to load departments", "error");
    }
  };

  const loadDesignations = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/Designation/get`, {}, {
        headers: getAuthHeader(),
      });
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setDesignations(data);
    } catch (error) {
      console.error("Error loading designations:", error);
    }
  };

  const loadPayrollEmployees = async () => {
    if (!payrollInfo) return;

    setLoading(true);
    try {
      const filter = {
        Payroll_Id: payrollInfo.payroll_Id
      };

      if (selectedBusiness) {
        filter.Business_Id = selectedBusiness.value;
      }

      if (selectedBranch) {
        filter.Branch_Id = selectedBranch.value;
      }

      if (selectedDepartment) {
        filter.Department_Id = selectedDepartment.value;
      }

      const payrollDetailsResponse = await getPayrollDetails(filter);
      const payrollDetailsData = Array.isArray(payrollDetailsResponse) ? payrollDetailsResponse : (payrollDetailsResponse.data || []);

      // If no payroll details exist, just set empty employees array (this is normal for a new payroll)
      if (!payrollDetailsData || payrollDetailsData.length === 0) {
        setEmployees([]);
        setSelectedEmployees([]);
        setSelectAll(false);
        setLoading(false);
        return;
      }

      // Get employee details for each payroll detail
      const employeeIds = payrollDetailsData.map(pd => pd.employee_Id).filter(id => id != null);

      // If no valid employee IDs, set empty array
      if (employeeIds.length === 0) {
        setEmployees([]);
        setSelectedEmployees([]);
        setSelectAll(false);
        setLoading(false);
        return;
      }

      const employeePromises = employeeIds.map(id => getEmployees({ employee_Id: id }));
      const employeeResults = await Promise.all(employeePromises);

      const enrichedEmployees = payrollDetailsData.map((pd, index) => {
        const employeeData = employeeResults[index]?.[0];
        if (!employeeData) {
          return null; // Skip if employee data not found
        }
        const designation = designations.find(d => d.designation_Id === employeeData?.employee_Designation_ID);

        // Use the saved advance balance from payroll detail record
        // This is already accurately calculated when employee was added
        const advanceBalance = pd.salaryAdvance_Amount || 0;

        return {
          ...pd,
          ...employeeData,
          employee_Name: employeeData?.employee_NameEn || employeeData?.employee_Name,
          designation_Name: designation ? (designation.designation_NameEn || designation.designation_Name) : 'N/A',
          serialNumber: index + 1,
          advanceBalance: advanceBalance
        };
      }).filter(emp => emp != null); // Remove null entries

      setEmployees(enrichedEmployees);
      setSelectedEmployees([]);
      setSelectAll(false);
    } catch (error) {
      console.error("Error loading payroll employees:", error);
      // Don't show error if it's just an empty response - this is normal for new payrolls
      if (error.response?.status !== 404 && !error.message?.includes('No payroll details')) {
        Swal.fire("Error", "Failed to load employees", "error");
      } else {
        // Empty payroll details is normal, just set empty array
        setEmployees([]);
        setSelectedEmployees([]);
        setSelectAll(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableEmployees = async () => {
    // Use selectedBusiness or session business
    const businessId = selectedBusiness?.value || session.businessId;
    if (!businessId || !payrollInfo) return;

    setLoading(true);
    try {
      const filter = {
        employee_Business_Id: businessId,
        OnWork: true
      };

      if (selectedBranch) {
        filter.employee_Branch_Id = selectedBranch.value;
      } else if (session.branchId && session.businessId) {
        // Use session branch if no selection
        filter.employee_Branch_Id = session.branchId;
      }

      if (selectedDepartment) {
        filter.employee_Department_ID = selectedDepartment.value;
      }

      const response = await getEmployees(filter);
      const employeeData = Array.isArray(response) ? response : (response.data || []);

      // Get existing payroll details to exclude employees already in payroll
      const payrollDetailsResponse = await getPayrollDetails({ Payroll_Id: payrollInfo.payroll_Id });
      const existingPayrollEmployees = new Set(
        (Array.isArray(payrollDetailsResponse) ? payrollDetailsResponse : (payrollDetailsResponse.data || [])).map(pd => pd.employee_Id)
      );

      // Calculate date range for unpaid leaves and salary advances
      const payrollDate = new Date(payrollInfo.payroll_Date);
      const startDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth() - 1, payrollDate.getDate());
      const endDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth(), payrollDate.getDate() - 1);

      console.log('Date Range for Payroll:', {
        payrollDate: payrollDate.toISOString(),
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });



      // Get employee leaves to find unpaid leaves within the date range
      const leavesResponse = await axios.post(`${BASE_URL}/api/EmployeeLeave/get`, {}, {
        headers: getAuthHeader(),
      });
      const leavesData = leavesResponse.data?.data || [];

      // Create a map of employee unpaid leaves within the date range
      const unpaidLeaveMap = new Map();
      leavesData.forEach(leave => {
        // Only count unpaid leaves (type 9) that are not cancelled
        if (leave.leave_LeaveType_Id === 9 &&
          !leave.is_Cancelled) {

          const leaveStartDate = new Date(leave.leave_FromDate);
          const leaveEndDate = new Date(leave.leave_ToDate);

          // Check if leave period overlaps with our target period
          if (leaveStartDate <= endDate && leaveEndDate >= startDate) {
            const employeeId = leave.leave_Employee_Id;
            const currentDays = unpaidLeaveMap.get(employeeId) || 0;

            // Calculate actual unpaid days within payroll period
            let effectiveEndDate;

            // If rejoin date exists, use it; otherwise employee is still on leave till payroll cutoff
            if (leave.leave_RejoinDate) {
              effectiveEndDate = new Date(leave.leave_RejoinDate);
            } else {
              effectiveEndDate = endDate; // Use payroll cutoff date since employee is still on unpaid leave
            }

            // The actual end date should not exceed payroll cutoff date
            const actualEndDate = effectiveEndDate > endDate ? endDate : effectiveEndDate;
            const actualStartDate = leaveStartDate < startDate ? startDate : leaveStartDate;

            // Calculate actual days within payroll period
            if (actualStartDate <= actualEndDate) {
              const actualDays = Math.ceil((actualEndDate - actualStartDate) / (1000 * 60 * 60 * 24)) + 1;
              const daysToAdd = leave.is_HalfDay ? actualDays * 0.5 : actualDays;
              unpaidLeaveMap.set(employeeId, currentDays + daysToAdd);
            }
          }
        }
      });





      // Function to calculate cumulative slab-based incentive for performance (type 1)


      // Format dates for API (YYYY-MM-DD format)
      const formatDateForAPI = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // Get absent days from DailyAttendance table for the payroll period
      const absentDaysFromAttendance = await getAbsentDaysForPayrollPeriod({
        StartDate: formatDateForAPI(startDate),
        EndDate: formatDateForAPI(endDate),
        Business_Id: selectedBusiness.value
      });

      // Create a map of absent days from DailyAttendance (Status = -1)
      const absentDaysMap = new Map();
      if (Array.isArray(absentDaysFromAttendance)) {
        absentDaysFromAttendance.forEach(item => {
          absentDaysMap.set(item.employeeID, item.absentDays || 0);
        });
      }

      // Get absent days and branches for mapping
      const [absentResponse, branchResponse] = await Promise.all([
        getAbsentDays({}),
        getBranches({ Business_Id: selectedBusiness.value })
      ]);

      const absentData = Array.isArray(absentResponse) ? absentResponse : (absentResponse.data || []);
      const branchData = Array.isArray(branchResponse) ? branchResponse : (branchResponse.data || []);
      const branchMap = new Map(branchData.map(b => [b.branch_Id, b.branch_NameEn || b.branch_Name]));

      // Include employees from both employee table and absent days table
      const absentEmployeeIds = absentData.map(a => a.absent_Employee_Id);
      const allEmployeeIds = [...new Set([...employeeData.map(e => e.employee_Id), ...absentEmployeeIds])];

      // Get employee details for all unique IDs
      const allEmployees = [];
      for (const empId of allEmployeeIds) {
        const emp = employeeData.find(e => e.employee_Id === empId);
        if (emp && !existingPayrollEmployees.has(empId)) {
          const branchName = branchMap.get(emp.employee_Branch_Id);
          const designation = designations.find(d => d.designation_Id === emp.employee_Designation_ID);
          const unpaidLeaveDays = unpaidLeaveMap.get(empId) || 0;

          // Get absent days from DailyAttendance table (Status = -1)
          // This is the calculated absent days for the payroll period
          const calculatedAbsentDays = absentDaysMap.get(empId) || 0;


          allEmployees.push({
            ...emp,
            serialNumber: allEmployees.length + 1,
            employee_Name: emp.employee_NameEn || emp.employee_Name,
            business_Name: selectedBusiness.label,
            branch_Name: branchName || 'N/A',
            designation_Name: designation ? (designation.designation_NameEn || designation.designation_Name) : 'N/A',
            basic_Salary: emp.employee_BS || 0,
            hra: emp.employee_HRA || 0,
            transport_Allowance: emp.employee_TA || 0,
            other_Allowance: emp.employee_OA || 0,
            food_Allowance: emp.employee_FoodAllowance || 0,
            communication_Allowance: emp.employee_CommunicationAllowance || 0,
            utility_Allowance: emp.employee_UtilityAllowance || 0,
            wps_Salary: emp.wpsSalary || 0,
            gross_Salary: emp.employee_GS || 0,
            absent_Days: calculatedAbsentDays || 0, // Use calculated absent days from DailyAttendance

          });
        }
      }

      setAvailableEmployees(allEmployees);

    } catch (error) {
      console.error("Error loading available employees:", error);
      Swal.fire("Error", "Failed to load available employees", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleBusinessChange = (selected) => {
    setSelectedBusiness(selected);
    setSelectedBranch(null);
    setBranches([]);
    setEmployees([]);
    setHasAppliedFilter(false);
    if (selected) {
      loadBranches(selected.value);
    }
  };

  const handleBranchChange = (selected) => {
    setSelectedBranch(selected);
    setSelectedDepartment(null);
    setDepartments([]);
    if (selected) {
      loadDepartments(selected.value);
    }
  };

  const handleDepartmentChange = (selected) => {
    setSelectedDepartment(selected);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    loadPayrollEmployees();
  };

  const handleAddEmployee = () => {
    if (!selectedBusiness) {
      Swal.fire("Warning", "Please select a business first", "warning");
      return;
    }
    setShowAddEmployeeModal(true);
    loadAvailableEmployees();
  };

  // Select All for main payroll table
  const handleSelectAllPayroll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      // Only select employees that are not approved
      const selectableEmployeeIds = employees
        .filter(emp => !emp.isApproved)
        .map(emp => emp.employee_Id);
      setSelectedEmployees(selectableEmployeeIds);
    } else {
      setSelectedEmployees([]);
    }
  };

  // Select All for add employee modal
  const handleSelectAllModal = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const selectableEmployeeIds = availableEmployees
        .map(emp => emp.employee_Id);
      setSelectedEmployees(selectableEmployeeIds);
    } else {
      setSelectedEmployees([]);
    }
  };

  const handleEmployeeSelect = (employeeId) => {
    const employee = employees.find(emp => emp.employee_Id === employeeId);
    if (employee?.isApproved) return; // Don't allow selection of approved employees

    setSelectedEmployees(prev => {
      const newSelection = prev.includes(employeeId)
        ? prev.filter(id => id !== employeeId)
        : [...prev, employeeId];

      // Update selectAll state based on whether all selectable employees are selected
      const selectableEmployees = employees.filter(emp => !emp.isApproved);
      setSelectAll(newSelection.length === selectableEmployees.length && selectableEmployees.length > 0);

      return newSelection;
    });
  };

  const handleAbsentDaysChange = (employeeId, value) => {
    // Normalize the value: remove leading zeros (except if value is just "0")
    let normalizedValue = value;
    if (value !== '' && value !== null && value !== undefined) {
      const stringValue = value.toString();
      // Remove leading zeros, but allow "0" as a valid value
      if (stringValue.length > 1 && stringValue.startsWith('0')) {
        normalizedValue = stringValue.replace(/^0+/, '') || '0';
      } else {
        normalizedValue = stringValue;
      }
    }

    setAvailableEmployees(prev =>
      prev.map(emp =>
        emp.employee_Id === employeeId
          ? { ...emp, absent_Days: normalizedValue }
          : emp
      )
    );
  };

  const handleAddToPayroll = async () => {
    if (selectedEmployees.length === 0) {
      Swal.fire("Warning", "Please select at least one employee", "warning");
      return;
    }

    setLoading(true);
    try {
      // Prepare employee list with absent days
      const employeeList = selectedEmployees.map(employeeId => {
        const employee = availableEmployees.find(emp => emp.employee_Id === employeeId);
        return {
          employee_Id: employeeId,
          absent_Days: employee.absent_Days || 0
        };
      });

      // Save absent days first (keep this for audit trail)
      for (const emp of employeeList) {
        const absentDay = {
          Absent_Employee_Id: emp.employee_Id,
          Absent_Days: emp.absent_Days,
          Absent_ModifiedDate: new Date().toISOString(),
          Absent_ModifiedBy: Number(localStorage.getItem("userId")) || 1
        };
        await saveAbsentDay(absentDay);
      }

      // Call the optimized backend API (calculation + insert in one call)
      console.log('Calling batch payroll calculation API for employees:', employeeList);
      const calculationResult = await calculatePayrollData(employeeList);

      if (!calculationResult.success) {
        throw new Error(`Batch calculation failed: ${calculationResult.message}`);
      }

      Swal.fire("Success",
        `Successfully processed ${calculationResult.data.processedEmployees} employees`,
        "success");

      setShowAddEmployeeModal(false);
      setSelectedEmployees([]);
      setSelectAll(false);
      loadPayrollEmployees(); // Refresh main list

    } catch (error) {
      console.error("Error adding to payroll:", error);
      Swal.fire("Error", error.message || "Failed to add to payroll", "error");
    } finally {
      setLoading(false);
    }
  };

  const payrollColumns = [
    {
      header: ({ table }) => (
        hasApprovePermission ? (
          <div className="form-check">
            <Input
              type="checkbox"
              className="form-check-input"
              checked={selectAll}
              onChange={handleSelectAllPayroll}
            />
          </div>
        ) : null
      ),
      accessorKey: "select",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        hasApprovePermission ? (
          <div className="form-check">
            <Input
              type="checkbox"
              className="form-check-input"
              checked={selectedEmployees.includes(row.original.employee_Id)}
              onChange={() => handleEmployeeSelect(row.original.employee_Id)}
            />
          </div>
        ) : null
      ),
      size: 50
    },
    {
      header: "Sr No",
      accessorKey: "serialNumber",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => row.original.serialNumber,
      size: 70
    },
    {
      header: "Employee Code",
      accessorKey: "employee_Code",
      enableColumnFilter: false,
    },
    {
      header: "Name",
      accessorKey: "employee_Name",
      enableColumnFilter: false,
    },
    {
      header: "Designation",
      accessorKey: "designation_Name",
      enableColumnFilter: false,
    },
    {
      header: "Absent Days",
      accessorKey: "absent_Days",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.absent_Days === 0 ? "-" : row.original.absent_Days,
    },
    {
      header: "Gross Salary",
      accessorKey: "gross_Salary",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.gross_Salary?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      header: "Absent Deduction",
      accessorKey: "absent_Deduction",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.absent_Deduction?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      header: "Performance Incentive",
      accessorKey: "performance_Incentive",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.performance_Incentive?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
    },
    {
      header: "Sales Incentive",
      accessorKey: "sales_Incentive",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.sales_Incentive?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
    },
    {
      header: "Net Salary",
      accessorKey: "net_Salary",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.net_Salary?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      header: "Advance Balance",
      accessorKey: "advanceBalance",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.advanceBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
    },
    {
      header: "Actions",
      accessorKey: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="d-flex gap-2">
          {row.original.isApproved ? (
            hasPermission && (
              <Button
                color="success"
                size="sm"
                onClick={() => handleManageClick(row.original)}
              >
                View
              </Button>
            )
          ) : (
            hasEditOrHigher && (
              <Button
                color="primary"
                size="sm"
                onClick={() => handleManageClick(row.original)}
              >
                Manage
              </Button>
            )
          )}
          {row.original.isApproved && (hasEditOrHigher || hasPermission) && (
            <Button
              color="info"
              size="sm"
              onClick={() => handlePrintPayslip(row.original)}
            >
              Print
            </Button>
          )}
        </div>
      ),
    },
  ];

  const availableEmployeeColumns = [
    {
      header: ({ table }) => (
        <div className="form-check">
          <Input
            type="checkbox"
            className="form-check-input"
            checked={selectAll}
            onChange={handleSelectAllModal}
          />
        </div>
      ),
      accessorKey: "select",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="form-check">
          <Input
            type="checkbox"
            className="form-check-input"
            checked={selectedEmployees.includes(row.original.employee_Id)}
            onChange={() => handleEmployeeSelect(row.original.employee_Id)}
          />
        </div>
      ),
      size: 50
    },
    {
      header: "Sr No",
      accessorKey: "serialNumber",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => row.original.serialNumber,
      size: 70
    },
    {
      header: "Employee Code",
      accessorKey: "employee_Code",
      enableColumnFilter: false,
    },
    {
      header: "Name",
      accessorKey: "employee_Name",
      enableColumnFilter: false,
    },
    {
      header: "Designation",
      accessorKey: "designation_Name",
      enableColumnFilter: false,
    },
    {
      header: "Gross Salary",
      accessorKey: "gross_Salary",
      enableColumnFilter: false,
      cell: ({ row }) => row.original.gross_Salary?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    },
    {
      header: "Absent Days",
      accessorKey: "absent_Days",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div style={{ minWidth: "120px" }}>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={row.original.absent_Days === 0 || row.original.absent_Days === '0' ? '' : (row.original.absent_Days ?? '')}
            onChange={(e) => handleAbsentDaysChange(row.original.employee_Id, e.target.value)}
            className="form-control"
            style={{ width: "100%" }}
          />
        </div>
      ),
      size: 120
    },
  ];

  const formatDate = (dateString, showDay = true) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (showDay) {
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    }
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  const handleSalaryAdvanceDeductionChange = (employeeId, value) => {
    // Only update state, don't save to backend
    setEmployees(prevEmployees => {
      const updatedEmployees = prevEmployees.map(emp => {
        if (emp.employee_Id === employeeId) {
          const updatedEmployee = {
            ...emp,
            salaryAdvance_Deduction: value
          };
          // Only calculate net salary if value is a valid number
          if (value !== '') {
            const numericValue = parseFloat(value) || 0;
            updatedEmployee.net_Salary = calculateNetSalary({
              ...updatedEmployee,
              salaryAdvance_Deduction: numericValue
            });
          }
          return updatedEmployee;
        }
        return emp;
      });
      return updatedEmployees;
    });
  };

  const handleOtherAdditionsChange = (employeeId, value) => {
    // Only update state, don't save to backend
    setEmployees(prevEmployees => {
      const updatedEmployees = prevEmployees.map(emp => {
        if (emp.employee_Id === employeeId) {
          const updatedEmployee = {
            ...emp,
            other_Additions: value
          };
          if (value !== '') {
            const numericValue = parseFloat(value) || 0;
            updatedEmployee.net_Salary = calculateNetSalary({
              ...updatedEmployee,
              other_Additions: numericValue
            });
          }
          return updatedEmployee;
        }
        return emp;
      });
      return updatedEmployees;
    });
  };

  const handleOtherDeductionsChange = (employeeId, value) => {
    // Only update state, don't save to backend
    setEmployees(prevEmployees => {
      const updatedEmployees = prevEmployees.map(emp => {
        if (emp.employee_Id === employeeId) {
          const updatedEmployee = {
            ...emp,
            other_Deductions: value
          };
          if (value !== '') {
            const numericValue = parseFloat(value) || 0;
            updatedEmployee.net_Salary = calculateNetSalary({
              ...updatedEmployee,
              other_Deductions: numericValue
            });
          }
          return updatedEmployee;
        }
        return emp;
      });
      return updatedEmployees;
    });
  };

  const calculateNetSalary = (employee) => {
    const grossSalary = employee.gross_Salary || 0; // Gross salary without WPS (WPS is just payment method)
    const absentDeduction = employee.absent_Deduction || 0;
    const salaryAdvanceDeduction = parseFloat(employee.salaryAdvance_Deduction) || 0;
    const otherAdditions = parseFloat(employee.other_Additions) || 0;
    const otherDeductions = parseFloat(employee.other_Deductions) || 0;
    const performanceIncentive = parseFloat(employee.performance_Incentive) || 0;
    const salesIncentive = parseFloat(employee.sales_Incentive) || 0;

    return grossSalary + performanceIncentive + salesIncentive - absentDeduction - salaryAdvanceDeduction + otherAdditions - otherDeductions;
  };

  const handleApproveChanges = async () => {
    if (selectedEmployees.length === 0) {
      Swal.fire("Warning", "Please select at least one employee", "warning");
      return;
    }

    setLoading(true);
    try {
      console.log(`Processing changes for ${selectedEmployees.length} employees at ${new Date().toISOString()}`);

      for (const employeeId of selectedEmployees) {
        const employee = employees.find(emp => emp.employee_Id === employeeId);
        if (employee) {
          const payloadData = {
            Payroll_Id: employee.payroll_Id,
            Employee_Id: employee.employee_Id,
            Business_Id: employee.business_Id || selectedBusiness.value,
            Branch_Id: employee.branch_Id || selectedBranch?.value,
            Department_Id: employee.department_Id || selectedDepartment?.value,
            Absent_Days: employee.absent_Days || 0,
            Basic_Salary: employee.basic_Salary || 0,
            HRA: employee.hra || 0,
            Transport_Allowance: employee.transport_Allowance || 0,
            Other_Allowance: employee.other_Allowance || 0,
            Food_Allowance: employee.food_Allowance || 0,
            Communication_Allowance: employee.communication_Allowance || 0,
            Utility_Allowance: employee.utility_Allowance || 0,
            WPS_Salary: employee.wpsSalary || 0,
            Gross_Salary: employee.gross_Salary || 0,
            Absent_Deduction: employee.absent_Deduction || 0,
            SalaryAdvance_Amount: employee.salaryAdvance_Amount || 0,
            SalaryAdvance_Deduction: parseFloat(employee.salaryAdvance_Deduction) || 0,
            Performance_Incentive: parseFloat(employee.performance_Incentive) || 0,
            Sales_Incentive: parseFloat(employee.sales_Incentive) || 0,
            Other_Additions: parseFloat(employee.other_Additions) || 0,
            Other_Deductions: parseFloat(employee.other_Deductions) || 0,
            Net_Salary: calculateNetSalary(employee),
            IsApproved: false,
            IsPayslip_Generated: false,
            Modified_By: Number(localStorage.getItem("userId")) || 1,
            Modified_Date: new Date().toISOString()
          };

          console.log('Sending payload to savePayrollDetail:', payloadData);
          const response = await savePayrollDetail(payloadData);
          if (response.msg && response.msg.toLowerCase().includes('error')) {
            throw new Error(response.msg);
          }
        }
      }

      // Clear all filters and reset state
      setSelectedBusiness(null);
      setSelectedBranch(null);
      setSelectedDepartment(null);
      setBranches([]);
      setDepartments([]);
      setEmployees([]);
      setSelectedEmployees([]);
      setSelectAll(false);
      setHasAppliedFilter(false);

      Swal.fire("Success", "Successfully processed selected employees", "success");
    } catch (error) {
      console.error("Error processing changes:", error);
      Swal.fire("Error", error.message || "Failed to process changes", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add function to load additional components
  const loadAdditionalComponents = async () => {
    try {
      const response = await axios.post(`${BASE_URL}/api/AdditionalComponent/get`, {
        Component_Business_Id: selectedBusiness?.value
      }, {
        headers: getAuthHeader()
      });
      setAdditionalComponents(response.data || []);
    } catch (error) {
      console.error("Error loading additional components:", error);
      Swal.fire("Error", "Failed to load additional components", "error");
    }
  };

  // Add function to load payroll other details
  const loadPayrollOtherDetails = async (employeeId) => {
    try {
      const response = await axios.post(`${BASE_URL}/api/PayrollOtherDetail/get`, {
        Payroll_Id: payrollInfo?.payroll_Id,
        Employee_Id: employeeId
      }, {
        headers: getAuthHeader()
      });

      const details = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      console.log('Loaded payroll other details:', details);
      setPayrollOtherDetails(details);
    } catch (error) {
      console.error("Error loading payroll other details:", error);
      Swal.fire("Error", "Failed to load payroll other details", "error");
    }
  };

  const handleManageClick = async (employee) => {
    setSelectedEmployee(employee);
    await loadAdditionalComponents();
    await loadPayrollOtherDetails(employee.employee_Id);
    setShowManageModal(true);
  };

  const handleSaveManageModal = async (updatedData) => {
    try {
      setLoading(true);

      if (!selectedEmployee) {
        throw new Error('No employee selected');
      }

      // Calculate the updated payroll detail
      const payrollDetail = {
        Payroll_Id: selectedEmployee.payroll_Id,
        Employee_Id: selectedEmployee.employee_Id,
        Business_Id: selectedEmployee.business_Id || selectedBusiness.value,
        Branch_Id: selectedEmployee.branch_Id || selectedBranch?.value,
        Department_Id: selectedEmployee.department_Id || selectedDepartment?.value,
        Absent_Days: selectedEmployee.absent_Days || 0,
        Basic_Salary: selectedEmployee.basic_Salary || 0,
        HRA: selectedEmployee.hra || 0,
        Transport_Allowance: selectedEmployee.transport_Allowance || 0,
        Other_Allowance: selectedEmployee.other_Allowance || 0,
        Food_Allowance: selectedEmployee.food_Allowance || 0,
        Communication_Allowance: selectedEmployee.communication_Allowance || 0,
        Utility_Allowance: selectedEmployee.utility_Allowance || 0,
        WPS_Salary: selectedEmployee.wpsSalary || 0,
        Gross_Salary: selectedEmployee.gross_Salary || 0,
        Absent_Deduction: selectedEmployee.absent_Deduction || 0,
        SalaryAdvance_Amount: selectedEmployee.salaryAdvance_Amount || 0,
        SalaryAdvance_Deduction: parseFloat(updatedData.salaryAdvanceDeduction) || 0,
        Performance_Incentive: parseFloat(updatedData.performanceIncentive) || 0,
        Sales_Incentive: parseFloat(updatedData.salesIncentive) || 0,
        Other_Additions: updatedData.totalAdditions || 0,
        Other_Deductions: updatedData.totalDeductions || 0,
        Net_Salary: updatedData.netSalary || 0,
        IsApproved: false,
        IsPayslip_Generated: selectedEmployee.isPayslip_Generated || false,
        Modified_By: Number(localStorage.getItem("userId")) || 1,
        Modified_Date: new Date().toISOString()
      };

      // Save payroll detail with all updated fields
      const response = await savePayrollDetail(payrollDetail);
      if (response.msg && response.msg.toLowerCase().includes('error')) {
        throw new Error(response.msg);
      }

      // Save other details
      for (const detail of updatedData.otherDetails) {
        const otherDetail = {
          Payroll_Id: selectedEmployee.payroll_Id,
          Employee_Id: selectedEmployee.employee_Id,
          Component_Id: detail.Component_Id,
          Amount: parseFloat(detail.Amount) || 0,
          Is_Deduction: detail.Is_Deduction,
          Notes: detail.Notes || '',
          Modified_By: Number(localStorage.getItem("userId")) || 1,
          Modified_Date: new Date().toISOString()
        };

        // Call API to save other detail
        const otherDetailResponse = await axios.post(`${BASE_URL}/api/PayrollOtherDetail/save`, otherDetail, {
          headers: getAuthHeader()
        });

        if (otherDetailResponse.data.msg && otherDetailResponse.data.msg.toLowerCase().includes('error')) {
          throw new Error(otherDetailResponse.data.msg);
        }
      }

      // Refresh the employees list to show updated values
      loadPayrollEmployees();

      Swal.fire({
        title: "Success",
        text: "Successfully saved changes",
        icon: "success",
        showConfirmButton: false,
        timer: 1500
      });

      setShowManageModal(false);

    } catch (error) {
      console.error("Error saving manage modal:", error);
      Swal.fire("Error", error.message || "Failed to save changes", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add function to handle approval of payroll details
  const handleApprovePayroll = async (employeeIds) => {
    try {
      const result = await Swal.fire({
        title: "Approve Payroll Details",
        text: `Are you sure you want to approve payroll details for ${employeeIds.length} employee(s)?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#28a745",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, approve!"
      });

      if (result.isConfirmed) {
        setLoading(true);

        for (const employeeId of employeeIds) {
          const employee = employees.find(emp => emp.employee_Id === employeeId);
          if (employee) {
            const payrollDetail = {
              ...employee,
              IsApproved: true,
              Approved_By: Number(localStorage.getItem("userId")) || 1,
              Approved_Date: new Date().toISOString(),
              Modified_By: Number(localStorage.getItem("userId")) || 1,
              Modified_Date: new Date().toISOString()
            };

            const response = await savePayrollDetail(payrollDetail);
            if (response.msg && response.msg.toLowerCase().includes('error')) {
              throw new Error(response.msg);
            }
          }
        }

        await loadPayrollEmployees();
        Swal.fire("Success", "Successfully approved payroll details", "success");
      }
    } catch (error) {
      console.error("Error approving payroll:", error);
      Swal.fire("Error", error.message || "Failed to approve payroll details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add function to handle approval of all payroll details
  const handleApproveAllPayroll = async () => {
    const unapprovedEmployees = employees.filter(emp => !emp.isApproved);

    if (unapprovedEmployees.length === 0) {
      Swal.fire("Info", "No unapproved employees found", "info");
      return;
    }

    await handleApprovePayroll(unapprovedEmployees.map(emp => emp.employee_Id));
  };

  // Add PayrollManageModal component
  const PayrollManageModal = ({ isOpen, toggle, employee, additionalComponents, payrollOtherDetails, payrollInfo, onSave }) => {
    const [salaryAdvanceDeduction, setSalaryAdvanceDeduction] = useState(employee?.salaryAdvance_Deduction || 0);
    const [selectedComponents, setSelectedComponents] = useState([]);
    const [totalAdditions, setTotalAdditions] = useState(0);
    const [totalDeductions, setTotalDeductions] = useState(0);
    const [netSalary, setNetSalary] = useState(employee?.net_Salary || 0);
    const [validationError, setValidationError] = useState(null);
    const [showAdditions, setShowAdditions] = useState(false);
    const [showDeductions, setShowDeductions] = useState(false);
    const [showAdditionDropdown, setShowAdditionDropdown] = useState(false);
    const [showDeductionDropdown, setShowDeductionDropdown] = useState(false);
    const [salaryAdvanceBalance, setSalaryAdvanceBalance] = useState(0);
    const [employeeLeaves, setEmployeeLeaves] = useState([]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [showLeaveDetails, setShowLeaveDetails] = useState(false);
    const [performanceIncentive, setPerformanceIncentive] = useState(employee?.performance_Incentive || 0);
    const [salesIncentive, setSalesIncentive] = useState(employee?.sales_Incentive || 0);

    const additionRef = useRef(null);
    const deductionRef = useRef(null);

    // Separate additions and deductions
    const availableAdditions = additionalComponents.filter(comp => !comp.component_IsDeduction);
    const availableDeductions = additionalComponents.filter(comp => comp.component_IsDeduction);

    useEffect(() => {
      // Initialize incentive values from employee data when modal opens
      if (employee) {
        setPerformanceIncentive(employee.performance_Incentive || 0);
        setSalesIncentive(employee.sales_Incentive || 0);
      }

      if (employee && payrollOtherDetails?.length > 0) {
        console.log('Initializing modal with payrollOtherDetails:', payrollOtherDetails);

        // Map the existing details to our component format
        const existingComponents = payrollOtherDetails.map(pod => {
          const matchingComponent = additionalComponents.find(ac => ac.component_Id === pod.component_Id);
          if (!matchingComponent) return null;

          return {
            Component_Id: pod.component_Id,
            Component_Name: matchingComponent.component_NameEn || matchingComponent.component_Name,
            Is_Deduction: matchingComponent.component_IsDeduction,
            Amount: pod.amount || 0,
            Notes: pod.notes || ''
          };
        }).filter(comp => comp !== null);

        console.log('Mapped existing components:', existingComponents);
        setSelectedComponents(existingComponents);
        calculateTotals(existingComponents, employee.salaryAdvance_Deduction || 0);
        loadSalaryAdvanceBalance();
        loadEmployeeLeaves();
      } else {
        setSelectedComponents([]);
        calculateTotals([], employee?.salaryAdvance_Deduction || 0);
        loadSalaryAdvanceBalance();
        loadEmployeeLeaves();
      }
    }, [employee, additionalComponents, payrollOtherDetails]);

    const loadSalaryAdvanceBalance = async () => {
      try {
        if (!employee?.employee_Id) return;

        // Use the saved advance amount and deduction from the payroll detail record
        // These are already calculated accurately when the employee was added
        const savedAdvanceAmount = employee.salaryAdvance_Amount || 0;
        const savedAdvanceDeduction = employee.salaryAdvance_Deduction || 0;

        // Set the balance from saved record
        setSalaryAdvanceBalance(savedAdvanceAmount);

        // Set the deduction from saved record
        if (salaryAdvanceDeduction !== savedAdvanceDeduction) {
          setSalaryAdvanceDeduction(savedAdvanceDeduction);
          calculateTotals(selectedComponents, savedAdvanceDeduction);
        }
      } catch (error) {
        console.error("Error loading salary advance balance:", error);
      }
    };

    const loadEmployeeLeaves = async () => {
      try {
        if (!employee?.employee_Id || !payrollInfo?.payroll_Date) return;

        // Calculate date range for the payroll period
        const payrollDate = new Date(payrollInfo.payroll_Date);
        const startDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth() - 1, payrollDate.getDate() + 1);
        const endDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth(), payrollDate.getDate());

        console.log('Payroll date range for leaves:', {
          payrollDate: payrollDate.toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        });

        // Load leave types first
        const leaveTypesResponse = await getLeaveTypes({});
        const leaveTypesData = Array.isArray(leaveTypesResponse) ? leaveTypesResponse : (leaveTypesResponse.data || []);
        setLeaveTypes(leaveTypesData);

        // Load employee leaves for the payroll period
        const leavesResponse = await getEmployeeLeaves({
          leave_Employee_Id: employee.employee_Id
        });

        const leavesData = Array.isArray(leavesResponse) ? leavesResponse : (leavesResponse.data || []);

        console.log('Raw leaves data:', leavesData);

        // Filter leaves by date range and remove cancelled leaves, then calculate payroll period days
        const validLeaves = leavesData.filter(leave => {
          // Skip cancelled leaves
          if (leave.is_Cancelled) return false;

          // Check if leave falls within payroll period using rejoin date logic
          const leaveFromDate = new Date(leave.leave_FromDate);
          const actualEndDate = leave.leave_RejoinDate ? new Date(leave.leave_RejoinDate) : new Date(leave.leave_ToDate);

          // Leave should overlap with payroll period (using actual end date)
          const isInRange = (leaveFromDate <= endDate && actualEndDate >= startDate);

          console.log('Checking leave:', {
            leaveId: leave.leave_Id,
            fromDate: leaveFromDate.toISOString(),
            originalToDate: new Date(leave.leave_ToDate).toISOString(),
            rejoinDate: leave.leave_RejoinDate ? new Date(leave.leave_RejoinDate).toISOString() : 'null',
            actualEndDate: actualEndDate.toISOString(),
            isInRange: isInRange
          });

          return isInRange;
        }).map(leave => {
          // Calculate effective dates and days based on rejoin date logic
          const leaveFromDate = new Date(leave.leave_FromDate);
          const rejoinDate = leave.leave_RejoinDate ? new Date(leave.leave_RejoinDate) : null;
          const actualLeaveEndDate = rejoinDate || new Date(leave.leave_ToDate);

          // Calculate effective start and end dates within payroll period
          const effectiveStartDate = leaveFromDate < startDate ? startDate : leaveFromDate;
          const effectiveEndDate = actualLeaveEndDate > endDate ? endDate : actualLeaveEndDate;

          // Calculate base days within payroll period
          let payrollPeriodDays = Math.max(0, (effectiveEndDate - effectiveStartDate) / (1000 * 60 * 60 * 24) + 1);

          // Apply half-day logic only if rejoin date falls within this payroll period
          if (leave.is_HalfDay && rejoinDate && rejoinDate >= startDate && rejoinDate <= endDate) {
            // Subtract 0.5 for half day when rejoin date is in current payroll period
            payrollPeriodDays = Math.max(0, payrollPeriodDays - 0.5);
          }

          return {
            ...leave,
            effectiveStartDate: effectiveStartDate,
            effectiveEndDate: effectiveEndDate,
            payrollPeriodDays: payrollPeriodDays
          };
        });

        console.log('Filtered valid leaves with payroll period calculations:', validLeaves);
        setEmployeeLeaves(validLeaves);
      } catch (error) {
        console.error("Error loading employee leaves:", error);
      }
    };

    const calculateMaxAllowedDeduction = (currentAdvanceDeduction, currentComponents, excludeComponentId = null) => {
      const grossSalary = employee?.gross_Salary || 0;
      const absentDeduction = employee?.absent_Deduction || 0;

      // Calculate total from other components excluding the one being edited (if any)
      const otherComponentsTotal = currentComponents
        .filter(comp => comp.Component_Id !== excludeComponentId)
        .reduce((sum, comp) => {
          const amount = parseFloat(comp.Amount) || 0;
          return sum + (comp.Is_Deduction ? -amount : amount);
        }, 0);

      // Calculate total available amount for deduction
      const totalAvailable = grossSalary - absentDeduction + otherComponentsTotal;

      // If calculating for advance deduction, subtract other deductions
      if (excludeComponentId === null) {
        return Math.max(0, totalAvailable);
      }

      // If calculating for a component, subtract advance deduction
      return Math.max(0, totalAvailable - parseFloat(currentAdvanceDeduction));
    };

    const calculateTotals = (components, advanceDeduction, perfIncentive = null, salesInc = null) => {
      console.log('Calculating totals for components:', components);

      // Use provided values or current state values
      const perfInc = perfIncentive !== null ? perfIncentive : performanceIncentive;
      const salesIn = salesInc !== null ? salesInc : salesIncentive;

      const additions = components
        .filter(d => !d.Is_Deduction)
        .reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);

      const deductions = components
        .filter(d => d.Is_Deduction)
        .reduce((sum, d) => sum + (parseFloat(d.Amount) || 0), 0);

      console.log('Calculated additions:', additions, 'deductions:', deductions);

      setTotalAdditions(additions);
      setTotalDeductions(deductions);

      // Calculate net salary (WPS is NOT part of gross salary - it's just payment method)
      // Use provided or state values for incentives (allows user to edit them)
      const newNetSalary = (employee?.gross_Salary || 0)
        + (parseFloat(perfInc) || 0)
        + (parseFloat(salesIn) || 0)
        - (employee?.absent_Deduction || 0)
        - (parseFloat(advanceDeduction) || 0)
        + additions
        - deductions;

      setNetSalary(newNetSalary);

      // Validate net salary
      if (newNetSalary < 0) {
        setValidationError('Total deductions cannot exceed gross salary. Please adjust the values.');
        return false;
      }

      setValidationError(null);
      return true;
    };

    const handleComponentAdd = (componentId, isDeduction) => {
      if (!componentId) return;

      const component = additionalComponents.find(c => c.component_Id === parseInt(componentId));
      if (component) {
        const newComponent = {
          Component_Id: component.component_Id,
          Component_Name: component.component_NameEn,
          Is_Deduction: component.component_IsDeduction,
          Amount: 0,
          Notes: ''
        };
        const updatedComponents = [...selectedComponents, newComponent];
        setSelectedComponents(updatedComponents);
        calculateTotals(updatedComponents, salaryAdvanceDeduction);

        // Auto scroll to the newly added component
        setTimeout(() => {
          const ref = isDeduction ? deductionRef : additionRef;
          ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    };

    const handleComponentChange = (componentId, field, value) => {
      if (field === 'Amount') {
        const component = selectedComponents.find(c => c.Component_Id === componentId);
        if (component?.Is_Deduction) {
          const maxAllowed = calculateMaxAllowedDeduction(salaryAdvanceDeduction, selectedComponents, componentId);
          const numericValue = parseFloat(value) || 0;

          if (numericValue > maxAllowed) {
            setValidationError(
              `Cannot add deduction of ${numericValue.toFixed(2)}. ` +
              `Maximum allowed deduction is ${maxAllowed.toFixed(2)} ` +
              `(Current net salary: ${netSalary.toFixed(2)})`
            );
            return;
          }
        }
      }

      const updatedComponents = selectedComponents.map(comp => {
        if (comp.Component_Id === componentId) {
          return { ...comp, [field]: value };
        }
        return comp;
      });

      setSelectedComponents(updatedComponents);
      // Recalculate totals with current incentive values
      calculateTotals(updatedComponents, salaryAdvanceDeduction);
    };

    const handleAdvanceDeductionChange = (value) => {
      const numericValue = parseFloat(value) || 0;

      // First check: Cannot exceed salary advance balance
      if (numericValue > salaryAdvanceBalance) {
        setValidationError(
          `Cannot set advance deduction to ${numericValue.toFixed(2)}. ` +
          `Maximum allowed deduction is ${salaryAdvanceBalance.toFixed(2)} ` +
          `(Total salary advance balance)`
        );
        return;
      }

      // Second check: Cannot exceed max allowed deduction based on salary
      const maxAllowed = calculateMaxAllowedDeduction(0, selectedComponents);
      if (numericValue > maxAllowed) {
        setValidationError(
          `Cannot set advance deduction to ${numericValue.toFixed(2)}. ` +
          `Maximum allowed deduction is ${maxAllowed.toFixed(2)} ` +
          `(Current net salary: ${netSalary.toFixed(2)})`
        );
        return;
      }

      setValidationError(null);
      setSalaryAdvanceDeduction(value);
      // Recalculate totals with current incentive values
      calculateTotals(selectedComponents, value);
    };

    const handleSave = () => {
      if (validationError) {
        Swal.fire("Error", validationError, "error");
        return;
      }

      if (netSalary < 0) {
        Swal.fire("Error", "Net salary cannot be negative", "error");
        return;
      }

      onSave({
        salaryAdvanceDeduction,
        performanceIncentive: parseFloat(performanceIncentive) || 0,
        salesIncentive: parseFloat(salesIncentive) || 0,
        otherDetails: selectedComponents.map(comp => ({
          Payroll_Id: employee.payroll_Id,
          Employee_Id: employee.employee_Id,
          Component_Id: comp.Component_Id,
          Amount: parseFloat(comp.Amount) || 0,
          Is_Deduction: comp.Is_Deduction,
          Notes: comp.Notes || ''
        })),
        totalAdditions,
        totalDeductions,
        netSalary,
        isApproved: true // Set isApproved to true when saving
      });
    };

    // Get list of component IDs that are already selected
    const selectedComponentIds = selectedComponents.map(c => c.Component_Id);

    // Update button click handlers to include auto-scroll
    const handleVariablePayClick = () => {
      setShowAdditions(!showAdditions);
      setShowAdditionDropdown(!showAdditions);
      if (!showAdditions) {
        setShowDeductions(false);
        setShowDeductionDropdown(false);
        // Auto scroll to additions section
        setTimeout(() => {
          additionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    };

    const handleDeductionsClick = () => {
      setShowDeductions(!showDeductions);
      setShowDeductionDropdown(!showDeductions);
      if (!showDeductions) {
        setShowAdditions(false);
        setShowAdditionDropdown(false);
        // Auto scroll to deductions section
        setTimeout(() => {
          deductionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    };

    // When opening modal in view mode, show both sections by default
    useEffect(() => {
      if (employee?.isApproved) {
        setShowAdditions(true);
        setShowDeductions(true);
      }
    }, [employee]);

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          {employee?.isApproved ? 'View' : 'Manage'} Payroll Details - {employee?.employee_Name}
        </ModalHeader>
        <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
          {validationError && (
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-circle me-2"></i>
              {validationError}
            </div>
          )}
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Basic Salary</Label>
                  <Input value={employee?.basic_Salary?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>HRA</Label>
                  <Input value={employee?.hra?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Transport Allowance</Label>
                  <Input value={employee?.transport_Allowance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Other Allowance</Label>
                  <Input value={employee?.other_Allowance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Food Allowance</Label>
                  <Input value={employee?.food_Allowance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Communication Allowance</Label>
                  <Input value={employee?.communication_Allowance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Utility Allowance</Label>
                  <Input value={employee?.utility_Allowance?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>WPS Salary</Label>
                  <Input value={employee?.wpsSalary?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Performance Incentive</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={performanceIncentive}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setPerformanceIncentive(value);
                      calculateTotals(selectedComponents, salaryAdvanceDeduction, value, salesIncentive);
                    }}
                    readOnly={employee?.isApproved}
                    className="text-success"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Sales Incentive</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={salesIncentive}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      setSalesIncentive(value);
                      calculateTotals(selectedComponents, salaryAdvanceDeduction, performanceIncentive, value);
                    }}
                    readOnly={employee?.isApproved}
                    className="text-success"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Gross Salary</Label>
                  <Input value={employee?.gross_Salary?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'} disabled />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Absent Days</Label>
                  <Input value={employee?.absent_Days || 0} disabled />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Absent Deduction</Label>
                  <Input
                    value={employee?.absent_Deduction?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || ''}
                    disabled
                    className="text-danger"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Salary Advance Balance</Label>
                  <Input
                    value={salaryAdvanceBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    disabled
                    className="text-danger font-weight-bold"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Salary Advance Deduction</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={salaryAdvanceDeduction}
                    onChange={(e) => handleAdvanceDeductionChange(e.target.value)}
                    className="text-danger"
                    readOnly={employee?.isApproved}
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Leave Information</Label>
                  <div className="d-flex align-items-center">
                    <Input
                      value={`${employeeLeaves.reduce((total, leave) => total + (leave.payrollPeriodDays || 0), 0)} day(s) taken`}
                      disabled
                      className="me-2"
                    />
                    {employeeLeaves.length > 0 && (
                      <Button
                        color="outline-info"
                        size="sm"
                        onClick={() => setShowLeaveDetails(!showLeaveDetails)}
                      >
                        {showLeaveDetails ? 'Hide' : 'View'} Details
                      </Button>
                    )}
                  </div>
                </FormGroup>
              </Col>
            </Row>

            {/* NEW: Detailed Leave Information from Procedure */}
            <Row>
              <Col md={12}>
                <h6 className="text-primary mb-3">Leave Details (As Per Current Payroll)</h6>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <FormGroup>
                  <Label>Sick Leave - Full Paid Days</Label>
                  <Input
                    value={employee?.sick_Fullpaid_Days || 0}
                    disabled
                    className="text-success"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Sick Leave - Half Paid Days</Label>
                  <Input
                    value={employee?.sick_Halfpaid_Days || 0}
                    disabled
                    className="text-warning"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Sick Leave - Unpaid Days</Label>
                  <Input
                    value={employee?.sick_Unpaid_Days || 0}
                    disabled
                    className="text-danger"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Annual Leave Days</Label>
                  <Input
                    value={employee?.annualLeave_Days || 0}
                    disabled
                    className="text-info"
                  />
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={3}>
                <FormGroup>
                  <Label>Full Paid Amount</Label>
                  <Input
                    value={employee?.sick_Fullpaid_Days_Amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                    disabled
                    className="text-success"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Half Paid Amount</Label>
                  <Input
                    value={employee?.sick_Halfpaid_Days_Amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                    disabled
                    className="text-warning"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Unpaid Amount</Label>
                  <Input
                    value={employee?.sick_Unpaid_Days_Amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                    disabled
                    className="text-danger"
                  />
                </FormGroup>
              </Col>
              <Col md={3}>
                <FormGroup>
                  <Label>Annual Leave Amount</Label>
                  <Input
                    value={employee?.annualLeave_Days_Amount?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                    disabled
                    className="text-info"
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Leave Details Section */}
            {showLeaveDetails && employeeLeaves.length > 0 && (
              <Row className="mt-3">
                <Col md={12}>
                  <div className="card">
                    <div className="card-header">
                      <h6 className="mb-0">Leave Details for Payroll Period</h6>
                    </div>
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Leave Type</th>
                              <th>From Date</th>
                              <th>To Date</th>
                              <th>Days</th>
                            </tr>
                          </thead>
                          <tbody>
                            {employeeLeaves.map((leave, index) => {
                              const leaveType = leaveTypes.find(lt => lt.leaveType_Id === leave.leave_LeaveType_Id);
                              return (
                                <tr key={index}>
                                  <td>{leaveType ? (leaveType.leaveType_NameEn || leaveType.leaveType_Name) : 'Unknown'}</td>
                                  <td>{leave.effectiveStartDate ? leave.effectiveStartDate.toLocaleDateString('en-GB') : new Date(leave.leave_FromDate).toLocaleDateString('en-GB')}</td>
                                  <td>{leave.effectiveEndDate ? leave.effectiveEndDate.toLocaleDateString('en-GB') : new Date(leave.leave_ToDate).toLocaleDateString('en-GB')}</td>
                                  <td>{leave.payrollPeriodDays || 0}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            <div className="mt-4">
              <h5>Additional Components</h5>
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                To remove an existing addition or deduction, set its value to 0 and save the changes.
              </div>

              <div className="d-flex justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <Button
                    color={showAdditions ? "success" : "outline-success"}
                    onClick={handleVariablePayClick}
                    className="me-2"
                    disabled={employee?.isApproved}
                  >
                    Variable Pay
                  </Button>
                  <span className="text-success ms-2">
                    Total: {totalAdditions.toFixed(2)}
                  </span>
                </div>
                <div className="d-flex align-items-center">
                  <span className="text-danger me-2">
                    Total: {totalDeductions.toFixed(2)}
                  </span>
                  <Button
                    color={showDeductions ? "danger" : "outline-danger"}
                    onClick={handleDeductionsClick}
                    disabled={employee?.isApproved}
                  >
                    Deductions
                  </Button>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  {showAdditions && (
                    <div className="border p-3 mb-3" ref={additionRef}>
                      <h6 className="text-success">Variable Pay Details</h6>
                      {!employee?.isApproved && showAdditionDropdown && (
                        <FormGroup>
                          <Input
                            type="select"
                            onChange={(e) => handleComponentAdd(parseInt(e.target.value), false)}
                            value=""
                            className="text-success mb-3"
                          >
                            <option value="">Select Variable Pay</option>
                            {availableAdditions
                              .filter(comp => !selectedComponentIds.includes(comp.component_Id))
                              .map(comp => (
                                <option key={comp.component_Id} value={comp.component_Id}>
                                  {comp.component_NameEn}
                                </option>
                              ))
                            }
                          </Input>
                        </FormGroup>
                      )}
                      {selectedComponents
                        .filter(comp => !comp.Is_Deduction)
                        .map((comp) => (
                          <Row key={comp.Component_Id} className="mb-2">
                            <Col md={4}>
                              <FormGroup>
                                <Label>{comp.Component_Name}</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={comp.Amount}
                                  onChange={(e) => handleComponentChange(comp.Component_Id, 'Amount', e.target.value)}
                                  className="text-success"
                                  readOnly={employee?.isApproved}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={8}>
                              <FormGroup>
                                <Label>Notes</Label>
                                <Input
                                  type="text"
                                  value={comp.Notes}
                                  onChange={(e) => handleComponentChange(comp.Component_Id, 'Notes', e.target.value)}
                                  placeholder="Enter notes"
                                  readOnly={employee?.isApproved}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        ))
                      }
                    </div>
                  )}

                  {showDeductions && (
                    <div className="border p-3" ref={deductionRef}>
                      <h6 className="text-danger">Deduction Details</h6>
                      {!employee?.isApproved && showDeductionDropdown && (
                        <FormGroup>
                          <Input
                            type="select"
                            onChange={(e) => handleComponentAdd(parseInt(e.target.value), true)}
                            value=""
                            className="text-danger mb-3"
                          >
                            <option value="">Select Deduction</option>
                            {availableDeductions
                              .filter(comp => !selectedComponentIds.includes(comp.component_Id))
                              .map(comp => (
                                <option key={comp.component_Id} value={comp.component_Id}>
                                  {comp.component_NameEn}
                                </option>
                              ))
                            }
                          </Input>
                        </FormGroup>
                      )}
                      {selectedComponents
                        .filter(comp => comp.Is_Deduction)
                        .map((comp) => (
                          <Row key={comp.Component_Id} className="mb-2">
                            <Col md={4}>
                              <FormGroup>
                                <Label>{comp.Component_Name}</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={comp.Amount}
                                  onChange={(e) => handleComponentChange(comp.Component_Id, 'Amount', e.target.value)}
                                  className="text-danger"
                                  readOnly={employee?.isApproved}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={8}>
                              <FormGroup>
                                <Label>Notes</Label>
                                <Input
                                  type="text"
                                  value={comp.Notes}
                                  onChange={(e) => handleComponentChange(comp.Component_Id, 'Notes', e.target.value)}
                                  placeholder="Enter notes"
                                  readOnly={employee?.isApproved}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                        ))
                      }
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        </ModalBody>
        <ModalFooter>
          {validationError && (
            <div className="alert alert-danger mb-3 w-100">
              <i className="fas fa-exclamation-circle me-2"></i>
              {validationError}
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              <div>
                <strong>Net Salary: </strong>
                <span className="h5 mb-0">{netSalary.toFixed(2)}</span>
              </div>
              <div className="mt-2">
                <small className="text-muted">
                  <strong>By WPS:</strong> {(() => {
                    const wpsSalary = employee?.wpsSalary || 0;
                    const byWps = netSalary <= wpsSalary ? netSalary : wpsSalary;
                    return byWps.toFixed(2);
                  })()} |
                  <strong> In Hand:</strong> {(() => {
                    const wpsSalary = employee?.wpsSalary || 0;
                    const inHand = netSalary > wpsSalary ? (netSalary - wpsSalary) : 0;
                    return inHand.toFixed(2);
                  })()}
                </small>
              </div>
            </div>
            <div>
              <Button color="secondary" onClick={toggle} className="me-2">Close</Button>
              {!employee?.isApproved && (
                <Button color="primary" onClick={handleSave} disabled={!!validationError}>
                  Save Changes
                </Button>
              )}
            </div>
          </div>
        </ModalFooter>
      </Modal>
    );
  };

  // Function to convert number to words
  const numberToWords = (num) => {
    if (num === 0) return 'Zero';

    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const thousands = ['', 'Thousand', 'Million', 'Billion'];

    const convertHundreds = (n) => {
      let result = '';
      if (n >= 100) {
        result += ones[Math.floor(n / 100)] + ' Hundred ';
        n %= 100;
      }
      if (n >= 20) {
        result += tens[Math.floor(n / 10)] + ' ';
        n %= 10;
      } else if (n >= 10) {
        result += teens[n - 10] + ' ';
        return result.trim();
      }
      if (n > 0) {
        result += ones[n] + ' ';
      }
      return result.trim();
    };

    let integer = Math.floor(num);
    const decimal = Math.round((num - integer) * 100);

    if (integer === 0) {
      return decimal > 0 ? `${convertHundreds(decimal)} Fils Only` : 'Zero';
    }

    let result = '';
    let thousandIndex = 0;

    while (integer > 0) {
      const chunk = integer % 1000;
      if (chunk !== 0) {
        const chunkWords = convertHundreds(chunk);
        result = chunkWords + (thousands[thousandIndex] ? ' ' + thousands[thousandIndex] : '') + ' ' + result;
      }
      integer = Math.floor(integer / 1000);
      thousandIndex++;
    }

    result = result.trim();

    if (decimal > 0) {
      result += ` And ${convertHundreds(decimal)} Fils`;
    }

    return `${currencyCode} ${result} Only`;
  };

  // Add PayslipModal component
  const PayslipModal = ({ isOpen, toggle, employee, businessName, companyLogo }) => {
    if (!employee) return null;

    const [annualLeaveRemarks, setAnnualLeaveRemarks] = useState(null);

    // Debug log for logo
    console.log("PayslipModal - companyLogo:", companyLogo);
    console.log("PayslipModal - businessName:", businessName);
    console.log("PayslipModal - BASE_URL:", BASE_URL);
    if (companyLogo) {
      console.log("PayslipModal - Full logo URL would be:", `${BASE_URL}${companyLogo}`);
    }

    const formatAmount = (amount) => {
      return (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Get additional components from payrollOtherDetails
    const additionalEarnings = payrollOtherDetails
      .filter(detail => !detail.is_Deduction && detail.amount > 0)
      .map(detail => {
        const component = additionalComponents.find(c => c.component_Id === detail.component_Id);
        return {
          description: component?.component_NameEn || 'Additional Component',
          amount: detail.amount
        };
      });

    const additionalDeductions = payrollOtherDetails
      .filter(detail => detail.is_Deduction && detail.amount > 0)
      .map(detail => {
        const component = additionalComponents.find(c => c.component_Id === detail.component_Id);
        return {
          description: component?.component_NameEn || 'Additional Deduction',
          amount: detail.amount
        };
      });

    // Base earnings that have values > 0 (WPS is NOT an earning - it's just payment method)
    const earnings = [
      { description: "Basic Salary", amount: employee.basic_Salary },
      { description: "HRA", amount: employee.hra },
      { description: "Transport Allowance", amount: employee.transport_Allowance },
      { description: "Other Allowance", amount: employee.other_Allowance },
      { description: "Food Allowance", amount: employee.food_Allowance },
      { description: "Communication Allowance", amount: employee.communication_Allowance },
      { description: "Utility Allowance", amount: employee.utility_Allowance },
      { description: "Performance Incentive", amount: employee.performance_Incentive },
      { description: "Sales Incentive", amount: employee.sales_Incentive },
      // NEW: Add leave-related earnings
      ...(employee.sick_Fullpaid_Days_Amount > 0 ? [{
        description: `Sick Leave - Full Paid (${employee.sick_Fullpaid_Days} day${employee.sick_Fullpaid_Days === 1 ? '' : 's'})`,
        amount: employee.sick_Fullpaid_Days_Amount
      }] : []),
      ...(employee.sick_Halfpaid_Days_Amount > 0 ? [{
        description: `Sick Leave - Half Paid (${employee.sick_Halfpaid_Days} day${employee.sick_Halfpaid_Days === 1 ? '' : 's'})`,
        amount: employee.sick_Halfpaid_Days_Amount
      }] : []),
      ...(employee.annualLeave_Days_Amount > 0 ? [{
        description: `Leave Salary Payable (${employee.annualLeave_Days} day${employee.annualLeave_Days === 1 ? '' : 's'})`,
        amount: employee.annualLeave_Days_Amount
      }] : []),
      ...additionalEarnings
    ].filter(item => parseFloat(item.amount) > 0);

    // Base deductions that have values > 0
    const deductions = [
      ...(employee.absent_Days > 0 ? [{
        description: `Absent Deduction (${employee.absent_Days} day${employee.absent_Days === 1 ? '' : 's'})`,
        amount: employee.absent_Deduction
      }] : []),
      ...(employee.salaryAdvance_Deduction > 0 ? [{
        description: "Salary Advance Deduction",
        amount: employee.salaryAdvance_Deduction
      }] : []),
      // NEW: Add leave-related deductions
      ...(employee.sick_Unpaid_Days_Amount > 0 ? [{
        description: `Sick Leave - Unpaid (${employee.sick_Unpaid_Days} day${employee.sick_Unpaid_Days === 1 ? '' : 's'})`,
        amount: employee.sick_Unpaid_Days_Amount
      }] : []),
      ...additionalDeductions
    ].filter(item => parseFloat(item.amount) > 0);

    const earningsTotal = earnings.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const deductionsTotal = deductions.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const netPay = earningsTotal - deductionsTotal;

    // Get annual leave remarks if annual leave exists
    const getAnnualLeaveRemarks = async () => {
      if (!employee.annualLeave_Days_Amount || employee.annualLeave_Days_Amount <= 0) {
        return null;
      }

      try {
        // Load employee leaves to get actual dates
        const leavesResponse = await getEmployeeLeaves({
          leave_Employee_Id: employee.employee_Id
        });

        const leavesData = Array.isArray(leavesResponse) ? leavesResponse : (leavesResponse.data || []);

        // Calculate payroll period
        const payrollDate = new Date(payrollInfo?.payroll_Date);
        const startDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth() - 1, payrollDate.getDate() + 1);
        const endDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth(), payrollDate.getDate());

        // Find annual leaves within the payroll period (assuming annual leave type is typically 4, but we'll check for any leave type that has annual leave amount)
        const annualLeaves = leavesData.filter(leave => {
          if (leave.is_Cancelled) return false;

          const leaveFromDate = new Date(leave.leave_FromDate);
          const leaveToDate = new Date(leave.leave_ToDate);
          const isInRange = (leaveFromDate <= endDate && leaveToDate >= startDate);

          // Check if this is likely an annual leave (you may need to adjust this based on your leave type IDs)
          // For now, we'll assume any leave in the period with leave days could be annual leave
          return isInRange && leave.leave_Days > 0;
        });

        const employeeNameDisplay = employee.employee_NameEn || employee.employee_Name || 'Employee';

        if (annualLeaves.length > 0) {
          // Use the first annual leave found
          const leave = annualLeaves[0];
          const fromDate = new Date(leave.leave_FromDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
          const toDate = new Date(leave.leave_ToDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

          return `${employeeNameDisplay} was on annual leave from ${fromDate} to ${toDate}, and the leave salary has been duly paid.`;
        } else {
          // Fallback if no specific leave dates found
          const monthYear = formatDate(payrollInfo?.payroll_Date);
          return `${employeeNameDisplay} was on annual leave during ${monthYear}, and the leave salary has been duly paid.`;
        }
      } catch (error) {
        console.error("Error loading leave details for remarks:", error);
        // Fallback on error
        const employeeNameDisplay = employee.employee_NameEn || employee.employee_Name || 'Employee';
        const monthYear = formatDate(payrollInfo?.payroll_Date);
        return `${employeeNameDisplay} was on annual leave during ${monthYear}, and the leave salary has been duly paid.`;
      }
    };

    // Get sick leave remarks if sick leave exists
    const getSickLeaveRemarks = async () => {
      const sickRemarks = [];

      if (employee.sick_Fullpaid_Days_Amount > 0 || employee.sick_Halfpaid_Days_Amount > 0) {
        try {
          // Load employee leaves to get actual dates
          const leavesResponse = await getEmployeeLeaves({
            leave_Employee_Id: employee.employee_Id
          });

          const leavesData = Array.isArray(leavesResponse) ? leavesResponse : (leavesResponse.data || []);

          // Calculate payroll period
          const payrollDate = new Date(payrollInfo?.payroll_Date);
          const startDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth() - 1, payrollDate.getDate());
          const endDate = new Date(payrollDate.getFullYear(), payrollDate.getMonth(), payrollDate.getDate() - 1);

          // Find sick leaves within the payroll period
          const sickLeaves = leavesData.filter(leave => {
            if (leave.is_Cancelled) return false;

            const leaveFromDate = new Date(leave.leave_FromDate);
            const leaveToDate = new Date(leave.leave_ToDate);
            const isInRange = (leaveFromDate <= endDate && leaveToDate >= startDate);

            // Check if this is likely a sick leave (you may need to adjust this based on your leave type IDs)
            return isInRange && leave.leave_Days > 0;
          });

          const employeeNameDisplay = employee.employee_NameEn || employee.employee_Name || 'Employee';

          if (sickLeaves.length > 0) {
            // Add remarks for sick leaves found - avoid duplicates
            if (employee.sick_Fullpaid_Days_Amount > 0) {
              const leave = sickLeaves[0]; // Use first leave for full paid
              const fromDate = new Date(leave.leave_FromDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
              const toDate = new Date(leave.leave_ToDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
              sickRemarks.push(`${employeeNameDisplay} was on sick leave (full paid) from ${fromDate} to ${toDate}, and the leave salary has been duly paid.`);
            }
            if (employee.sick_Halfpaid_Days_Amount > 0) {
              const leave = sickLeaves[0]; // Use first leave for half paid
              const fromDate = new Date(leave.leave_FromDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
              const toDate = new Date(leave.leave_ToDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
              sickRemarks.push(`${employeeNameDisplay} was on sick leave (half paid) from ${fromDate} to ${toDate}, and the leave salary has been duly paid.`);
            }
          }
        } catch (error) {
          console.error("Error loading sick leave details for remarks:", error);
        }
      }

      return sickRemarks;
    };

    // Load annual leave remarks when component mounts or employee changes
    useEffect(() => {
      const loadRemarks = async () => {
        let allRemarks = [];

        // Load annual leave remarks
        if (employee && employee.annualLeave_Days_Amount > 0) {
          const annualRemarks = await getAnnualLeaveRemarks();
          if (annualRemarks) allRemarks.push(annualRemarks);
        }



        setAnnualLeaveRemarks(allRemarks.length > 0 ? allRemarks : null);
      };

      if (employee) {
        loadRemarks();
      } else {
        setAnnualLeaveRemarks(null);
      }
    }, [employee]);

    const handlePrint = async () => {
      const now = new Date();
      const formattedDate = now.toISOString().replace(/[-:]/g, '').replace(/\..+/, '');
      const fileName = `Payslip_${employee.employee_NameEn?.replace(/\s+/g, '') || employee.employee_Name?.replace(/\s+/g, '')}_${employee.employee_Code}_${formattedDate}`;

      const doc = new jsPDF();

      // Set font styles
      doc.setFont("helvetica");

      // Header
      let yPos = 20;

      // Add company logo if available
      if (companyLogo && companyLogo.trim() !== '') {
        try {
          console.log("Loading logo for PDF:", companyLogo);
          // Ensure companyLogo starts with / if it doesn't already
          const logoPath = companyLogo.startsWith('/') ? companyLogo : `/${companyLogo}`;
          // Use relative URL so request goes through dev proxy (same-origin)
          const logoUrl = logoPath;
          console.log("Logo URL (relative):", logoUrl);

          // Test if the URL is accessible
          console.log("Testing logo URL accessibility...");

          // Fetch logo as blob and convert to data URL
          const response = await fetch(logoUrl, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            headers: {
              'Accept': 'image/*'
            }
          });

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const blob = await response.blob();
          const dataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });

          // Load image to get dimensions
          const img = new Image();
          await new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            img.src = dataUrl;
          });

          console.log("Image loaded successfully, dimensions:", img.width, "x", img.height);

          // Calculate display dimensions for PDF (in mm)
          const maxWidth = 30;   // mm
          const maxHeight = 15;  // mm
          let displayWidth = maxWidth;
          let displayHeight = (img.height * maxWidth) / img.width;

          if (displayHeight > maxHeight) {
            displayWidth = (displayWidth * maxHeight) / displayHeight;
            displayHeight = maxHeight;
          }

          console.log("Final logo dimensions for PDF:", displayWidth, "x", displayHeight);

          // Determine image format from data URL
          let imageFormat = 'PNG';
          if (dataUrl.includes('data:image/jpeg') || dataUrl.includes('data:image/jpg')) {
            imageFormat = 'JPEG';
          } else if (dataUrl.includes('data:image/png')) {
            imageFormat = 'PNG';
          } else if (dataUrl.includes('data:image/gif')) {
            imageFormat = 'GIF';
          }

          console.log("Image format detected:", imageFormat);

          // Add image to PDF
          doc.addImage(
            dataUrl,
            imageFormat,
            (doc.internal.pageSize.width - displayWidth) / 2,
            yPos,
            displayWidth,
            displayHeight,
            '',
            'NONE'
          );

          yPos += displayHeight + 10; // Move down after logo
          console.log("Logo added to PDF at position:", yPos);
        } catch (error) {
          console.error("Error loading logo for PDF:", error);
          const logoPath = companyLogo.startsWith('/') ? companyLogo : `/${companyLogo}`;
          console.error("Logo URL that failed:", logoPath);

          // Try alternative approach using img element for CORS issues
          try {
            console.log("Attempting alternative logo loading method...");
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.src = `${BASE_URL}${logoPath}`;

            await new Promise((resolve, reject) => {
              img.onload = resolve;
              img.onerror = reject;
            });

            // Create canvas to convert image to data URL
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const dataUrl = canvas.toDataURL('image/png');

            // Calculate display dimensions for PDF (in mm)
            const maxWidth = 30;   // mm
            const maxHeight = 15;  // mm
            let displayWidth = maxWidth;
            let displayHeight = (img.height * maxWidth) / img.width;

            if (displayHeight > maxHeight) {
              displayWidth = (displayWidth * maxHeight) / displayHeight;
              displayHeight = maxHeight;
            }

            // Add image to PDF
            doc.addImage(
              dataUrl,
              'PNG',
              (doc.internal.pageSize.width - displayWidth) / 2,
              yPos,
              displayWidth,
              displayHeight,
              '',
              'NONE'
            );

            yPos += displayHeight + 10; // Move down after logo
            console.log("Logo added to PDF using alternative method at position:", yPos);
          } catch (altError) {
            console.error("Alternative logo loading also failed:", altError);
            yPos = 20; // Start at normal position if logo fails
          }
        }
      }

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(`${(businessName || 'COMPANY').toUpperCase()}`, doc.internal.pageSize.width / 2, yPos, { align: "center" });
      doc.setFont("helvetica", "normal");
      doc.text('PAYSLIP', doc.internal.pageSize.width / 2, yPos + 10, { align: "center" });

      doc.setFontSize(11);
      doc.text(formatDate(payrollInfo?.payroll_Date), doc.internal.pageSize.width / 2, yPos + 20, { align: "center" });

      // Employee Info
      doc.setFontSize(10);
      doc.text(`Employee Name: ${employee.employee_NameEn || employee.employee_Name}`, 20, yPos + 35);
      doc.text(`Employee Code: ${employee.employee_Code}`, doc.internal.pageSize.width - 60, yPos + 35);

      // Table Headers
      let tableYPos = yPos + 50;
      const xStart = 20;
      const colWidths = [15, 100, 40];

      // Helper function for table rows
      const addTableRow = (cols, isHeader = false, isBold = false) => {
        if (tableYPos > 270) { // Check if we need a new page
          doc.addPage();
          tableYPos = 20;
        }

        if (isBold) doc.setFont("helvetica", "bold");
        else doc.setFont("helvetica", "normal");

        let xPos = xStart;
        cols.forEach((text, i) => {
          if (i === 2) { // Amount column
            doc.text(text.toString(), xPos + colWidths[i], tableYPos, { align: "right" });
          } else {
            doc.text(text.toString(), xPos, tableYPos);
          }
          xPos += colWidths[i];
        });

        // Draw lines
        if (isHeader) {
          doc.line(xStart, tableYPos + 2, xStart + colWidths.reduce((a, b) => a + b), tableYPos + 2);
        }

        tableYPos += 7;
      };

      // Headers
      addTableRow(["S.NO", "DESCRIPTION", "AMOUNT"], true, true);

      // Earnings Section
      tableYPos += 3;
      addTableRow(["", "EARNINGS", ""], false, true);
      earnings.forEach((item, index) => {
        addTableRow([
          (index + 1).toString(),
          item.description,
          formatAmount(item.amount)
        ]);
      });

      // Earnings Total
      tableYPos += 2;
      addTableRow(["", "Sub Total", formatAmount(earningsTotal)], false, true);

      // Deductions Section
      tableYPos += 5;
      addTableRow(["", "DEDUCTIONS", ""], false, true);
      deductions.forEach((item, index) => {
        addTableRow([
          (index + 1).toString(),
          item.description,
          formatAmount(item.amount)
        ]);
      });

      // Deductions Total
      tableYPos += 2;
      addTableRow(["", "Sub Total", formatAmount(deductionsTotal)], false, true);

      // Net Pay
      tableYPos += 5;
      addTableRow(["", "NET PAY", formatAmount(netPay)], false, true);

      // WPS and In Hand breakdown
      tableYPos += 2;
      const wpsSalary = employee.wpS_Salary || employee.wpsSalary || 0;
      const byWps = netPay <= wpsSalary ? netPay : wpsSalary;
      const inHand = netPay > wpsSalary ? (netPay - wpsSalary) : 0;

      addTableRow(["1", "By WPS", formatAmount(byWps)]);
      addTableRow(["2", "In Hand", formatAmount(inHand)]);

      // Amount in Words
      tableYPos += 10;
      if (tableYPos > 240) {
        doc.addPage();
        tableYPos = 20;
      }
      doc.setFont("helvetica", "bold");
      doc.text("Amount in words:", 20, tableYPos);
      doc.setFont("helvetica", "normal");
      tableYPos += 5;

      // Split amount in words into multiple lines if needed
      const amountInWords = numberToWords(netPay);
      const maxWidth = doc.internal.pageSize.width - 40;
      const wrappedAmountText = doc.splitTextToSize(`(${amountInWords})`, maxWidth);
      doc.text(wrappedAmountText, 20, tableYPos);
      tableYPos += wrappedAmountText.length * 5;

      // Remarks section - Only add if annual leave exists
      if (annualLeaveRemarks) {
        tableYPos += 10;
        if (tableYPos > 240) {
          doc.addPage();
          tableYPos = 20;
        }
        doc.setFont("helvetica", "bold");
        doc.text("Remarks:", 20, tableYPos);
        doc.setFont("helvetica", "normal");
        tableYPos += 5;

        // Handle remarks as bullet points
        const remarks = Array.isArray(annualLeaveRemarks) ? annualLeaveRemarks : [annualLeaveRemarks];
        remarks.forEach((remark, index) => {
          // Add bullet point
          doc.text("•", 25, tableYPos);

          // Split remark into multiple lines if needed
          const wrappedRemarkText = doc.splitTextToSize(remark, maxWidth - 15);
          doc.text(wrappedRemarkText, 30, tableYPos);
          tableYPos += wrappedRemarkText.length * 5 + 2;

          // Check if we need a new page
          if (tableYPos > 260) {
            doc.addPage();
            tableYPos = 20;
          }
        });
      }

      // Signatures
      tableYPos += 15;
      if (tableYPos > 250) {
        doc.addPage();
        tableYPos = 30;
      }
      doc.text("Prepared By: Jijo Thomas", 20, tableYPos);
      doc.text("Received By: _________________", doc.internal.pageSize.width - 70, tableYPos);

      // Save PDF
      doc.save(`${fileName}.pdf`);
    };

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>
          Payslip - {employee.employee_NameEn || employee.employee_Name}
        </ModalHeader>
        <ModalBody>
          <div className="border p-4">
            <div className="text-center mb-4">
              {companyLogo && (
                <div className="mb-3">
                  <img
                    src={`${BASE_URL}${companyLogo}`}
                    alt="Company Logo"
                    style={{
                      maxHeight: '60px',
                      maxWidth: '200px',
                      objectFit: 'contain'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              <h4><strong>{(businessName || 'COMPANY').toUpperCase()}</strong></h4>
              <h4>PAYSLIP</h4>
              <p className="mb-1">{formatDate(payrollInfo?.payroll_Date)}</p>
            </div>

            <div className="row mb-3">
              <div className="col-12">
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <td><strong>Employee Name:</strong> {employee.employee_NameEn || employee.employee_Name}</td>
                      <td><strong>Employee Code:</strong> {employee.employee_Code}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th>S.NO</th>
                      <th>DESCRIPTION</th>
                      <th className="text-end">AMOUNT</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan="3"><strong>EARNINGS</strong></td>
                    </tr>
                    {earnings.map((item, index) => (
                      <tr key={`earning-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.description}</td>
                        <td className="text-end">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan="2" className="text-end"><strong>Sub Total</strong></td>
                      <td className="text-end"><strong>{formatAmount(earningsTotal)}</strong></td>
                    </tr>
                    <tr>
                      <td colSpan="3"><strong>DEDUCTIONS</strong></td>
                    </tr>
                    {deductions.map((item, index) => (
                      <tr key={`deduction-${index}`}>
                        <td>{index + 1}</td>
                        <td>{item.description}</td>
                        <td className="text-end">{formatAmount(item.amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan="2" className="text-end"><strong>Sub Total</strong></td>
                      <td className="text-end"><strong>{formatAmount(deductionsTotal)}</strong></td>
                    </tr>
                    <tr>
                      <td colSpan="2"><strong>NET PAY</strong></td>
                      <td className="text-end"><strong>{formatAmount(netPay)}</strong></td>
                    </tr>
                    <tr>
                      <td>1</td>
                      <td>By WPS</td>
                      <td className="text-end">{(() => {
                        const wpsSalary = employee.wpS_Salary || employee.wpsSalary || 0;
                        const byWps = netPay <= wpsSalary ? netPay : wpsSalary;
                        return formatAmount(byWps);
                      })()}</td>
                    </tr>
                    <tr>
                      <td>2</td>
                      <td>In Hand</td>
                      <td className="text-end">{(() => {
                        const wpsSalary = employee.wpS_Salary || employee.wpsSalary || 0;
                        const inHand = netPay > wpsSalary ? (netPay - wpsSalary) : 0;
                        return formatAmount(inHand);
                      })()}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Amount in Words Section */}
            <div className="row mt-4">
              <div className="col-12">
                <p><strong>Amount in words:</strong> ({numberToWords(netPay)})</p>
              </div>
            </div>

            {/* Remarks Section - Only show if annual leave exists */}
            {annualLeaveRemarks && (
              <div className="row mt-3">
                <div className="col-12">
                  <p><strong>Remarks:</strong></p>
                  <ul>
                    {Array.isArray(annualLeaveRemarks) ?
                      annualLeaveRemarks.map((remark, index) => (
                        <li key={index}>{remark}</li>
                      )) :
                      <li>{annualLeaveRemarks}</li>
                    }
                  </ul>
                </div>
              </div>
            )}

            <div className="row mt-4">
              <div className="col-6">
                <p><strong>Prepared By:</strong> Jijo Thomas</p>
              </div>
              <div className="col-6 text-end">
                <p><strong>Received By:</strong> _________________</p>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={() => handlePrint().catch(error => {
            console.error("Error in handlePrint:", error);
            Swal.fire("Error", "Failed to generate PDF", "error");
          })}>Print</Button>
          <Button color="secondary" onClick={toggle}>Close</Button>
        </ModalFooter>
      </Modal>
    );
  };

  const handlePrintPayslip = async (employee) => {
    try {
      setLoading(true);

      // Fetch fresh data for the employee
      const [payrollDetailResponse, otherDetailsResponse, componentsResponse, employeeResponse] = await Promise.all([
        getPayrollDetails({
          Payroll_Id: payrollInfo.payroll_Id,
          Employee_Id: employee.employee_Id
        }),
        axios.post(`${BASE_URL}/api/PayrollOtherDetail/get`, {
          Payroll_Id: payrollInfo.payroll_Id,
          Employee_Id: employee.employee_Id
        }, { headers: getAuthHeader() }),
        axios.post(`${BASE_URL}/api/AdditionalComponent/get`, {
          Component_Business_Id: selectedBusiness?.value
        }, { headers: getAuthHeader() }),
        getEmployees({ employee_Id: employee.employee_Id })
      ]);

      // Process the responses
      const payrollDetail = Array.isArray(payrollDetailResponse) ? payrollDetailResponse[0] : payrollDetailResponse.data[0];
      const otherDetails = otherDetailsResponse.data || [];
      const components = componentsResponse.data || [];
      const employeeDetail = Array.isArray(employeeResponse) ? employeeResponse[0] : employeeResponse.data[0];

      // Merge employee details with payroll detail
      const mergedEmployeeData = {
        ...payrollDetail,
        employee_NameEn: employeeDetail?.employee_NameEn,
        employee_Name: employeeDetail?.employee_NameEn || employeeDetail?.employee_Name,
        employee_Code: employeeDetail?.employee_Code
      };

      // Update the states
      setPayrollOtherDetails(otherDetails);
      setAdditionalComponents(components);
      setSelectedPayslipEmployee(mergedEmployeeData);
      setShowPayslipModal(true);

      // Debug log
      console.log("handlePrintPayslip - selectedBusiness:", selectedBusiness);
      console.log("handlePrintPayslip - mergedEmployeeData:", mergedEmployeeData);
      console.log("handlePrintPayslip - business_Id:", mergedEmployeeData.business_Id);
      const business = selectedBusiness || businesses.find(b => b.value === mergedEmployeeData.business_Id);
      const businessLogo = business?.business_LogoPath;
      const companyLogo = business?.company?.company_LogoPath;
      console.log("handlePrintPayslip - businessLogo:", businessLogo);
      console.log("handlePrintPayslip - companyLogo (fallback):", companyLogo);
      console.log("handlePrintPayslip - final logo:", businessLogo || companyLogo);
      console.log("handlePrintPayslip - businesses:", businesses);
    } catch (error) {
      console.error("Error preparing payslip:", error);
      Swal.fire("Error", "Failed to load payslip data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Calculate total net salary
  const totalNetSalary = employees.reduce((sum, emp) => sum + (emp.net_Salary || 0), 0);

  // Add new function to delete payroll details
  const handleDeletePayrollDetails = async (employeeIds) => {
    try {
      setLoading(true);

      for (const employeeId of employeeIds) {
        const response = await axios.post(
          `${BASE_URL}/api/PayrollDetail/delete`,
          {
            Payroll_Id: payrollInfo.payroll_Id,
            Employee_Id: employeeId
          },
          { headers: getAuthHeader() }
        );

        if (!response.data.msg || response.data.msg.toLowerCase().includes('error')) {
          throw new Error(`Failed to delete payroll detail for employee ${employeeId}`);
        }
      }

      Swal.fire("Success", "Successfully deleted selected employees from payroll", "success");
      loadPayrollEmployees(); // Refresh the list
    } catch (error) {
      console.error("Error deleting payroll details:", error);
      Swal.fire("Error", error.message || "Failed to delete payroll details", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add function to check if all employees are approved
  const checkAllEmployeesApproved = () => {
    return !employees.some(emp => !emp.isApproved);
  };

  const handleDateChange = (dateStr) => {
    if (!dateStr) {
      setSelectedPayrollDate(null);
      setSelectedFromDate(null);
      return;
    }

    const selectedDate = new Date(dateStr);
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const day = selectedDate.getDate();

    // To Date: Input Date at 23:59:59
    const toDate = new Date(year, month, day);
    toDate.setHours(23, 59, 59, 0);

    // From Date: 1 Calendar Month back Logic
    // Example: If To Date is Jan 29, From Date should be (End Date + 1 Day) - 1 Month = Dec 30
    let fromDate = new Date(year, month, day);
    fromDate.setDate(fromDate.getDate() + 1); // Go to "Day After"
    fromDate.setMonth(fromDate.getMonth() - 1); // Go back 1 calendar month
    fromDate.setHours(0, 0, 0, 0);

    // Helper to format as local YYYY-MM-DDTHH:mm:ss
    const toLocalString = (d) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    };

    setSelectedFromDate(toLocalString(fromDate));
    setSelectedPayrollDate(toLocalString(toDate));
  };

  const handleMonthChange = (monthStr) => {
    if (!monthStr) {
      setSelectedPayrollDate(null);
      setSelectedFromDate(null);
      return;
    }
    // monthStr format is "YYYY-MM"
    const [year, month] = monthStr.split('-').map(Number);

    const from = new Date(year, month - 1, 1);
    const to = new Date(year, month, 0); // last day of the month

    const toLocalString = (d, h, m, s) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(h)}:${pad(m)}:${pad(s)}`;
    };

    setSelectedFromDate(toLocalString(from, 0, 0, 0));
    setSelectedPayrollDate(toLocalString(to, 23, 59, 59));
  };

  // Handle date selection confirmation
  const handleDateSelectionConfirm = async () => {
    if (!selectedPayrollDate) {
      Swal.fire("Warning", "Please select a payroll date", "warning");
      return;
    }

    try {
      setLoading(true);
      const userId = Number(localStorage.getItem("userId")) || 1;

      if (dateSelectionMode === 'create') {
        // Create first payroll
        const response = await createFirstPayroll(selectedPayrollDate, selectedFromDate, isMonthly, userId);
        if (response.success) {
          Swal.fire("Success", response.message || "First payroll created successfully", "success");
          setShowDateSelectionModal(false);
          setSelectedPayrollDate(null);
          setSelectedFromDate(null);
          setIsMonthly(false);
          await loadCurrentPayroll();
        } else {
          Swal.fire("Error", response.message || "Failed to create payroll", "error");
        }
      } else if (dateSelectionMode === 'update') {
        // Update existing payroll date
        if (!payrollInfo?.payroll_Id) {
          Swal.fire("Error", "Payroll information not found", "error");
          return;
        }
        const response = await updatePayrollDate(payrollInfo.payroll_Id, selectedPayrollDate, selectedFromDate, isMonthly, userId);
        if (response.success) {
          Swal.fire("Success", response.message || "Payroll date updated successfully", "success");
          setShowDateSelectionModal(false);
          setSelectedPayrollDate(null);
          setSelectedFromDate(null);
          setIsMonthly(false);
          setCurrentPayrollDateForUpdate(null);
          await loadCurrentPayroll();
        } else {
          Swal.fire("Error", response.message || "Failed to update payroll date", "error");
        }
      }
    } catch (error) {
      console.error("Error in date selection:", error);
      Swal.fire("Error", error.response?.data?.message || error.message || "Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle skip payroll
  const handleSkipPayroll = async () => {
    if (!payrollInfo?.payroll_Id) {
      Swal.fire("Error", "No active payroll found", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Skip Payroll",
      html: "This will mark the current payroll as posted and create the next month's payroll.<br/><br/>No employee records will be created for the skipped month.<br/><br/>Are you sure you want to continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, skip it!",
      cancelButtonText: "Cancel"
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        const userId = Number(localStorage.getItem("userId")) || 1;
        const skipDate = new Date().toISOString();

        const response = await skipPayroll(payrollInfo.payroll_Id, userId, skipDate);

        if (response.success) {
          Swal.fire({
            title: "Success",
            text: response.message || "Payroll skipped successfully",
            icon: "success"
          });

          // Refresh payroll info
          await loadCurrentPayroll();

          // Clear filters and reset state
          setSelectedBusiness(null);
          setSelectedBranch(null);
          setSelectedDepartment(null);
          setBranches([]);
          setDepartments([]);
          setEmployees([]);
          setSelectedEmployees([]);
          setSelectAll(false);
          setHasAppliedFilter(false);
        } else {
          Swal.fire("Error", response.message || "Failed to skip payroll", "error");
        }
      } catch (error) {
        console.error("Error skipping payroll:", error);
        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to skip payroll", "error");
      } finally {
        setLoading(false);
      }
    }
  };

  // Modify handlePostPayroll to check for unapproved employees
  const handlePostPayroll = async () => {
    try {
      console.log("Starting post payroll process...");

      if (!payrollInfo?.payroll_Id) {
        console.log("No active payroll found:", payrollInfo);
        Swal.fire("Error", "No active payroll found", "error");
        return;
      }

      // Check for unapproved employees
      const unapprovedEmployees = employees.filter(emp => !emp.isApproved);
      console.log("Checking unapproved employees:", unapprovedEmployees);

      if (unapprovedEmployees.length > 0) {
        console.log("Found unapproved employees:", unapprovedEmployees.length);
        Swal.fire({
          title: "Cannot Post Payroll",
          text: "There are unapproved employees. Please approve or delete them before posting.",
          icon: "warning"
        });
        return;
      }

      const result = await Swal.fire({
        title: "Post Payroll",
        text: "Are you sure you want to post this payroll? This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, post it!"
      });

      if (result.isConfirmed) {
        setLoading(true);
        console.log("User confirmed post payroll action");

        // Prepare payload according to PayrollPostRequest model
        const payload = {
          Payroll_Id: payrollInfo.payroll_Id,
          Approved_By: Number(localStorage.getItem("userId")) || 1,
          Approved_Date: new Date().toISOString()
        };

        console.log("Posting payroll with payload:", payload);

        // Make API call to post payroll using the correct endpoint
        const response = await axios.post(
          `${BASE_URL}/api/Payroll/post`,
          payload,
          { headers: getAuthHeader() }
        );

        console.log("Post payroll API response:", response.data);

        if (!response.data.success) {
          throw new Error(response.data.message);
        }

        // Refresh payroll info
        await loadCurrentPayroll();

        Swal.fire({
          title: "Success",
          text: response.data.message || "Payroll has been posted successfully",
          icon: "success"
        });

        // Clear filters and reset state
        setSelectedBusiness(null);
        setSelectedBranch(null);
        setSelectedDepartment(null);
        setBranches([]);
        setDepartments([]);
        setEmployees([]);
        setSelectedEmployees([]);
        setSelectAll(false);
        setHasAppliedFilter(false);
      } else {
        console.log("User cancelled post payroll action");
      }
    } catch (error) {
      console.error("Error posting payroll:", error);
      Swal.fire("Error", error.message || "Failed to post payroll", "error");
    } finally {
      setLoading(false);
    }
  };

  // Add new button for deleting selected employees
  const handleDeleteSelected = async () => {
    if (selectedEmployees.length === 0) {
      Swal.fire("Warning", "Please select employees to delete", "warning");
      return;
    }

    const result = await Swal.fire({
      title: "Delete Selected Employees",
      text: "Are you sure you want to delete the selected employees from payroll?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete them!"
    });

    if (result.isConfirmed) {
      await handleDeletePayrollDetails(selectedEmployees);
    }
  };

  // Add new button for deleting all unapproved
  const handleDeleteAllUnapproved = async () => {
    const unapprovedEmployees = employees.filter(emp => !emp.isApproved);

    if (unapprovedEmployees.length === 0) {
      Swal.fire("Info", "No unapproved employees found", "info");
      return;
    }

    const result = await Swal.fire({
      title: "Delete All Unapproved",
      text: `Are you sure you want to delete all ${unapprovedEmployees.length} unapproved employees from payroll?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all!"
    });

    if (result.isConfirmed) {
      await handleDeletePayrollDetails(unapprovedEmployees.map(emp => emp.employee_Id));
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="card-title mb-0">Payroll Processing</h4>
              {payrollInfo && (
                <h5 className="text-muted mb-0">
                  Processing Month: {formatDate(payrollInfo.payroll_Date)}
                </h5>
              )}
            </div>

            <Form>
              <Row>
                <Col md={3}>
                  <FormGroup>
                    <Label>Company</Label>
                    <Select
                      value={selectedBusiness}
                      onChange={handleBusinessChange}
                      options={businesses}
                      isClearable={!session.businessId}
                      isDisabled={!!session.businessId}
                      placeholder="Select Company"
                      maxMenuHeight={80}
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Branch</Label>
                    <Select
                      value={selectedBranch}
                      onChange={handleBranchChange}
                      options={branches}
                      isClearable={!session.branchId}
                      isDisabled={!!session.branchId || !selectedBusiness}
                      placeholder="Select Branch"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>Department</Label>
                    <Select
                      value={selectedDepartment}
                      onChange={handleDepartmentChange}
                      options={departments}
                      isClearable
                      isDisabled={!selectedBranch}
                      placeholder="Select Department"
                    />
                  </FormGroup>
                </Col>
                <Col md={3}>
                  <FormGroup>
                    <Label>&nbsp;</Label>
                    <div className="d-flex gap-2">
                      <Button
                        color="primary"
                        onClick={handleApplyFilter}
                      >
                        Apply Filter
                      </Button>
                      {hasEditOrHigher && (
                        <Button
                          color="success"
                          onClick={handleAddEmployee}
                        >
                          Add Employee
                        </Button>
                      )}
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </Form>

            {loading ? (
              <div className="text-center mt-4">
                <ClipLoader color="#123abc" loading={loading} size={50} />
              </div>
            ) : (
              <>
                {hasAppliedFilter && employees.length === 0 ? (
                  <div className="text-center mt-4">
                    <p>No employees found in payroll. Click "Add Employee" to add employees to payroll.</p>
                  </div>
                ) : (
                  <>
                    {employees.length > 0 && (
                      <>
                        <div style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "auto" }}>
                          <div className="table-responsive">
                            <Table className="table table-bordered table-nowrap custom-header-css mt-4">
                              <thead>
                                <tr>
                                  {hasApprovePermission && (
                                    <th>
                                      <div className="form-check">
                                        <Input
                                          type="checkbox"
                                          className="form-check-input"
                                          checked={selectAll}
                                          onChange={handleSelectAllPayroll}
                                        />
                                      </div>
                                    </th>
                                  )}
                                  <th>Sr No</th>
                                  <th>Employee Code</th>
                                  <th>Name</th>
                                  <th>Designation</th>
                                  <th>Absent Days</th>
                                  <th>Gross Salary</th>
                                  <th>Absent Deduction</th>
                                  <th>Performance Incentive</th>
                                  <th>Sales Incentive</th>
                                  <th>Net Salary</th>
                                  <th>Advance Balance</th>
                                  <th>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {employees.map((employee, index) => (
                                  <tr key={employee.employee_Id}>
                                    {hasApprovePermission && (
                                      <td>
                                        <div className="form-check">
                                          <Input
                                            type="checkbox"
                                            className="form-check-input"
                                            checked={selectedEmployees.includes(employee.employee_Id)}
                                            onChange={() => handleEmployeeSelect(employee.employee_Id)}
                                            disabled={employee.isApproved}
                                          />
                                        </div>
                                      </td>
                                    )}
                                    <td>{employee.serialNumber || index + 1}</td>
                                    <td>{employee.employee_Code}</td>
                                    <td>{employee.employee_Name}</td>
                                    <td>{employee.designation_Name}</td>
                                    <td>{employee.absent_Days === 0 ? "-" : employee.absent_Days}</td>
                                    <td>{employee.gross_Salary?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td>{employee.absent_Deduction?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td>{employee.performance_Incentive?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                                    <td>{employee.sales_Incentive?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                                    <td>{employee.net_Salary?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                    <td>{employee.advanceBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</td>
                                    <td>
                                      <div className="d-flex gap-2">
                                        {employee.isApproved ? (
                                          hasPermission && (
                                            <Button
                                              color="success"
                                              size="sm"
                                              onClick={() => handleManageClick(employee)}
                                            >
                                              View
                                            </Button>
                                          )
                                        ) : (
                                          hasEditOrHigher && (
                                            <Button
                                              color="primary"
                                              size="sm"
                                              onClick={() => handleManageClick(employee)}
                                            >
                                              Manage
                                            </Button>
                                          )
                                        )}
                                        {employee.isApproved && (hasEditOrHigher || hasPermission) && (
                                          <Button
                                            color="info"
                                            size="sm"
                                            onClick={() => handlePrintPayslip(employee)}
                                          >
                                            Print
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mt-3">
                          <div className="text-end">
                            <h5>Total Net Salary: {totalNetSalary.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</h5>
                          </div>
                          <div className="d-flex gap-2">
                            {selectedEmployees.length > 0 && (
                              <>
                                {hasDeletePermission && (
                                  <Button
                                    color="danger"
                                    onClick={handleDeleteSelected}
                                  >
                                    Delete Selected ({selectedEmployees.length})
                                  </Button>
                                )}
                                {hasApprovePermission && (
                                  <Button
                                    color="success"
                                    onClick={() => handleApprovePayroll(selectedEmployees)}
                                  >
                                    Approve Selected ({selectedEmployees.length})
                                  </Button>
                                )}
                              </>
                            )}
                            {hasDeletePermission && (
                              <Button
                                color="warning"
                                onClick={handleDeleteAllUnapproved}
                              >
                                Delete All Unapproved
                              </Button>
                            )}
                            {hasApprovePermission && (
                              <Button
                                color="success"
                                onClick={handleApproveAllPayroll}
                              >
                                Approve All Unapproved
                              </Button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Skip/Post Payroll buttons - shown whenever an active payroll exists */}
                {payrollInfo && (
                  <div className="d-flex justify-content-end gap-2 mt-3">
                    {hasApprovePermission && (
                      <>
                        {/* Show Skip button only when no employees are processed */}
                        {employees.length === 0 && (
                          <Button
                            color="warning"
                            size="lg"
                            className="px-4"
                            onClick={handleSkipPayroll}
                            disabled={!payrollInfo || payrollInfo.payroll_IsPosted || loading}
                            style={{
                              fontWeight: "bold",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                            }}
                          >
                            Skip Payroll
                          </Button>
                        )}
                        {/* Show Post Payroll only when employees are processed */}
                        {employees.length > 0 && (
                          <Button
                            color="primary"
                            size="lg"
                            className="px-4"
                            onClick={handlePostPayroll}
                            disabled={!payrollInfo || payrollInfo.payroll_IsPosted || loading}
                            style={{
                              backgroundColor: "#0056b3",
                              borderColor: "#0056b3",
                              fontWeight: "bold",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                            }}
                          >
                            Post Payroll
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>

        {/* Add Employee Modal */}
        <Modal isOpen={showAddEmployeeModal} size="xl" toggle={() => setShowAddEmployeeModal(false)}>
          <ModalHeader toggle={() => setShowAddEmployeeModal(false)}>
            Add Employees to Payroll
          </ModalHeader>
          <ModalBody style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {loading ? (
              <div className="text-center">
                <ClipLoader color="#123abc" loading={loading} size={50} />
              </div>
            ) : (
              <>
                {availableEmployees.length > 0 ? (
                  <div className="table-responsive">
                    <Table className="table table-bordered table-nowrap">
                      <thead>
                        <tr>
                          <th>
                            <div className="form-check">
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectAll}
                                onChange={handleSelectAllModal}
                              />
                            </div>
                          </th>
                          <th>Sr No</th>
                          <th>Employee Code</th>
                          <th>Name</th>
                          <th>Designation</th>
                          <th>Gross Salary</th>
                          <th>Absent Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {availableEmployees.map((employee, index) => (
                          <tr key={employee.employee_Id}>
                            <td>
                              <div className="form-check">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedEmployees.includes(employee.employee_Id)}
                                  onChange={() => handleEmployeeSelect(employee.employee_Id)}
                                />
                              </div>
                            </td>
                            <td>{index + 1}</td>
                            <td>{employee.employee_Code}</td>
                            <td>{employee.employee_Name}</td>
                            <td>{employee.designation_Name}</td>
                            <td>{employee.gross_Salary?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td>
                              <Input
                                type="number"
                                min="0"
                                step="0.5"
                                value={employee.absent_Days === 0 || employee.absent_Days === '0' ? '' : (employee.absent_Days ?? '')}
                                onChange={(e) => handleAbsentDaysChange(employee.employee_Id, e.target.value)}
                                style={{ width: '80px' }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center">No available employees to add to payroll.</p>
                )}
              </>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleAddToPayroll}
              disabled={selectedEmployees.length === 0 || loading}
            >
              Add to Payroll ({selectedEmployees.length} selected)
            </Button>
            <Button color="secondary" onClick={() => setShowAddEmployeeModal(false)}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        {/* Payroll Manage Modal */}
        <PayrollManageModal
          isOpen={showManageModal}
          toggle={() => setShowManageModal(false)}
          employee={selectedEmployee}
          additionalComponents={additionalComponents}
          payrollOtherDetails={payrollOtherDetails}
          payrollInfo={payrollInfo}
          onSave={handleSaveManageModal}
        />

        {/* Payslip Modal */}
        <PayslipModal
          isOpen={showPayslipModal}
          toggle={() => setShowPayslipModal(false)}
          employee={selectedPayslipEmployee}
          businessName={selectedBusiness?.label || businesses.find(b => b.value === selectedPayslipEmployee?.business_Id)?.label}
          companyLogo={(() => {
            // Get business logo with fallback to company logo
            const business = selectedBusiness || businesses.find(b => b.value === selectedPayslipEmployee?.business_Id);
            const businessLogo = business?.business_LogoPath;
            const companyLogo = business?.company?.company_LogoPath;
            return businessLogo || companyLogo || null;
          })()}
        />

        {/* Date Selection Modal */}
        <Modal isOpen={showDateSelectionModal} toggle={() => { }} backdrop="static" keyboard={false}>
          <ModalHeader>
            {dateSelectionMode === 'create' ? 'Select First Payroll Date' : 'Update Payroll Date'}
          </ModalHeader>
          <ModalBody>
            <div className="mb-4">
              <p className="text-muted">
                {dateSelectionMode === 'create'
                  ? 'No payroll exists in the system. Please select the first payroll period to begin processing.'
                  : 'The current payroll date is outdated and no employees have been processed yet.'}
              </p>

              {dateSelectionMode === 'update' && currentPayrollDateForUpdate && (
                <div className="alert alert-info mb-3 py-2">
                  <strong>Last Payroll Date:</strong> {formatDate(currentPayrollDateForUpdate)}
                </div>
              )}

              <FormGroup check className="mb-3 p-0">
                <div className="d-flex align-items-center bg-light p-2 rounded border">
                  <Input
                    type="checkbox"
                    id="isMonthlyCheckbox"
                    checked={isMonthly}
                    onChange={(e) => {
                      const newValue = e.target.checked;
                      setIsMonthly(newValue);
                      // Clear dates when toggling for a clean start
                      setSelectedPayrollDate(null);
                      setSelectedFromDate(null);
                    }}
                    style={{ marginLeft: '10px', cursor: 'pointer', transform: 'scale(1.2)' }}
                  />
                  <Label for="isMonthlyCheckbox" className="mb-0 ms-3" style={{ cursor: 'pointer', fontWeight: 'bold', color: '#333' }}>
                    Monthly Payroll (01 to Last Day)
                  </Label>
                </div>
                <small className="text-muted d-block mt-1">
                  If checked, salary will be calculated for full month based on 30 working days.
                </small>
              </FormGroup>

              {isMonthly ? (
                /* Monthly Scenario: Show Month/Year Picker */
                <FormGroup>
                  <Label className="fw-bold">Select Payroll Month <span className="text-danger">*</span></Label>
                  <Input
                    type="month"
                    onChange={(e) => handleMonthChange(e.target.value)}
                    value={selectedPayrollDate ? (() => {
                      const dateObj = new Date(selectedPayrollDate);
                      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                      const year = dateObj.getFullYear();
                      return `${year}-${month}`;
                    })() : ''}
                    disabled={loading}
                    required
                  />
                  <small className="text-muted d-block mt-1">
                    Choosing a month will automatically set the period from the 1st to the last day.
                  </small>
                </FormGroup>
              ) : (
                /* Non-Monthly Scenario: Show To-Date selection, From-Date auto calculated */
                <div className="row">
                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="fw-bold">To Date (End Date) <span className="text-danger">*</span></Label>
                      <Input
                        type="date"
                        value={selectedPayrollDate ? selectedPayrollDate.split('T')[0] : ''}
                        onChange={(e) => handleDateChange(e.target.value)}
                        min={dateSelectionMode === 'update' && currentPayrollDateForUpdate ? (() => {
                          const minDate = new Date(currentPayrollDateForUpdate);
                          minDate.setDate(minDate.getDate() + 1);
                          return minDate.toISOString().split('T')[0];
                        })() : null}
                        disabled={loading}
                        required
                      />
                    </FormGroup>
                  </div>
                  <div className="col-md-6">
                    <FormGroup>
                      <Label className="fw-bold">From Date (Start Date)</Label>
                      <Input
                        type="date"
                        value={selectedFromDate ? selectedFromDate.split('T')[0] : ''}
                        disabled={true} // Auto-calculated
                        className="bg-light"
                      />
                    </FormGroup>
                  </div>
                </div>
              )}

              {selectedPayrollDate && selectedFromDate && (
                <div className="mt-3 p-3 border rounded bg-white shadow-sm">
                  <div className="d-flex align-items-center mb-2">
                    <div className="rounded-circle bg-primary bg-soft p-2 me-2">
                      <i className="mdi mdi-calendar-range text-primary"></i>
                    </div>
                    <span className="fw-bold">Payroll Period Summary</span>
                  </div>
                  <div className="row g-0">
                    <div className="col-5">
                      <div className="text-muted small">From</div>
                      <div className="fw-bold">{formatDate(selectedFromDate)}</div>
                    </div>
                    <div className="col-2 d-flex align-items-center justify-content-center text-muted">
                      <i className="mdi mdi-arrow-right"></i>
                    </div>
                    <div className="col-5 text-end">
                      <div className="text-muted small">To</div>
                      <div className="fw-bold">{formatDate(selectedPayrollDate)}</div>
                    </div>
                  </div>
                  <hr className="my-2" />
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="text-muted small">Total Duration:</span>
                    <span className="badge bg-primary rounded-pill">
                      {(() => {
                        const from = new Date(selectedFromDate);
                        const to = new Date(selectedPayrollDate);
                        // Strip hours for calculation
                        const f = new Date(from.getFullYear(), from.getMonth(), from.getDate());
                        const t = new Date(to.getFullYear(), to.getMonth(), to.getDate());
                        const diffTime = Math.abs(t - f);
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                        return `${diffDays} Days`;
                      })()}
                    </span>
                  </div>
                  <div className="mt-2 text-info small d-flex align-items-center">
                    <i className="mdi mdi-information-outline me-1"></i>
                    <span>Salary will be calculated on a 30-day base.</span>
                  </div>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <div>
              {dateSelectionMode === 'update' && (
                <Button
                  color="warning"
                  onClick={async () => {
                    if (!payrollInfo?.payroll_Id) {
                      Swal.fire("Error", "Payroll information not found", "error");
                      return;
                    }

                    const result = await Swal.fire({
                      title: "Skip Payroll",
                      html: "This will mark the current payroll as posted and create the next month's payroll.<br/><br/>No employee records will be created for the skipped month.<br/><br/>Are you sure you want to continue?",
                      icon: "warning",
                      showCancelButton: true,
                      confirmButtonColor: "#d33",
                      cancelButtonColor: "#3085d6",
                      confirmButtonText: "Yes, skip it!",
                      cancelButtonText: "Cancel"
                    });

                    if (result.isConfirmed) {
                      try {
                        setLoading(true);
                        const userId = Number(localStorage.getItem("userId")) || 1;
                        const skipDate = new Date().toISOString();

                        const response = await skipPayroll(payrollInfo.payroll_Id, userId, skipDate);

                        if (response.success) {
                          Swal.fire({
                            title: "Success",
                            text: response.message || "Payroll skipped successfully",
                            icon: "success"
                          });

                          // Close modal and refresh
                          setShowDateSelectionModal(false);
                          setSelectedPayrollDate(null);
                          setDateSelectionMode(null);
                          setCurrentPayrollDateForUpdate(null);

                          // Refresh payroll info
                          await loadCurrentPayroll();

                          // Clear filters and reset state
                          setSelectedBusiness(null);
                          setSelectedBranch(null);
                          setSelectedDepartment(null);
                          setBranches([]);
                          setDepartments([]);
                          setEmployees([]);
                          setSelectedEmployees([]);
                          setSelectAll(false);
                          setHasAppliedFilter(false);
                        } else {
                          Swal.fire("Error", response.message || "Failed to skip payroll", "error");
                        }
                      } catch (error) {
                        console.error("Error skipping payroll:", error);
                        Swal.fire("Error", error.response?.data?.message || error.message || "Failed to skip payroll", "error");
                      } finally {
                        setLoading(false);
                      }
                    }
                  }}
                  disabled={loading}
                  style={{
                    fontWeight: "bold"
                  }}
                >
                  Skip Payroll Instead
                </Button>
              )}
            </div>
            <div>
              <Button
                color="primary"
                onClick={handleDateSelectionConfirm}
                disabled={!selectedPayrollDate || loading}
              >
                {loading ? 'Processing...' : (dateSelectionMode === 'create' ? 'Create Payroll' : 'Update Date')}
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default PayrollProcessingPage; 